import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';
import RiskDetailsViewer from './components/RiskDetailsViewer';
import CodeAITab from './CodeAITab';

// Robustly read a JSON API response. Surfaces a non-JSON body (e.g. an HTML 404
// when the server hasn't been restarted, or an expired-login page) as a clear,
// actionable error instead of an opaque "Unexpected token '<'" JSON parse crash.
async function readJsonResponse(res) {
  const raw = await res.text();
  let data;
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    throw new Error(
      `Server returned a non-JSON response (HTTP ${res.status}). ` +
      `If you just updated Lyric, restart the server.`,
    );
  }
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (HTTP ${res.status})`);
  }
  return data;
}

const PullRequestForm = ({ selectedProject, selectedModel, isActive }) => {
  // Code AI sub-tab state
  const [codeAiSubTab, setCodeAiSubTab] = useState('pr-analysis'); // 'pr-analysis', 'bug-hunt', 'branch-review', 'file-analysis'

  // Branch Review state
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branchSearch, setBranchSearch] = useState('');
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branchAnalysis, setBranchAnalysis] = useState(null);
  const [isAnalyzingBranch, setIsAnalyzingBranch] = useState(false);
  const [branchError, setBranchError] = useState(null);

  // File Analysis state
  const [fileTree, setFileTree] = useState([]);
  const [fileTreePath, setFileTreePath] = useState('');
  const [fileTreeRef, setFileTreeRef] = useState('');
  const [selectedAnalysisFile, setSelectedAnalysisFile] = useState('');
  const [fileAnalysis, setFileAnalysis] = useState(null);
  const [isAnalyzingFile, setIsAnalyzingFile] = useState(false);
  const [fileAnalysisError, setFileAnalysisError] = useState(null);
  const [loadingFileTree, setLoadingFileTree] = useState(false);
  const [fileTreeBreadcrumb, setFileTreeBreadcrumb] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [repositories, setRepositories] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [repoSearch, setRepoSearch] = useState('');
  const [pullRequests, setPullRequests] = useState([]);
  const [loadingPRs, setLoadingPRs] = useState(false);
  const [prLoadError, setPrLoadError] = useState(null);
  const [selectedPR, setSelectedPR] = useState('');
  const [selectedPRTitle, setSelectedPRTitle] = useState('');
  const [aiReview, setAiReview] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  
  // Copilot instructions state
  const [localRulesFile, setLocalRulesFile] = useState(null);
  const [showRulesSummary, setShowRulesSummary] = useState(false);

  // Collapsible sections state
  const [guidelineRules, setGuidelineRules] = useState([]);

  // PR Report state
  const [prReport, setPrReport] = useState(null);
  const [prReportStats, setPrReportStats] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showPRReport, setShowPRReport] = useState(false);
  const [repositoriesCache, setRepositoriesCache] = useState(null);
  const [repositoriesCacheTime, setRepositoriesCacheTime] = useState(null);
  const [prFilter, setPrFilter] = useState('all'); // 'all', 'low', 'medium', 'high', 'critical'
  const [prReportDaysOffset, setPrReportDaysOffset] = useState(0); // Track which 10-day period we're viewing
  const [isLoadingPRReport, setIsLoadingPRReport] = useState(false); // Loading state for pagination
  const [showDateRangePicker, setShowDateRangePicker] = useState(false); // Show/hide date range picker
  const [dateRangeStart, setDateRangeStart] = useState(''); // Start date for custom range
  const [dateRangeEnd, setDateRangeEnd] = useState(''); // End date for custom range
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false); // Loading state for PDF download
  
  // Risk Analysis state
  const [riskSummary, setRiskSummary] = useState(null);
  const [riskViewMode, setRiskViewMode] = useState('summary'); // 'summary' or 'detailed'
  const [selectedPRRisk, setSelectedPRRisk] = useState(null);
  const [showRiskModal, setShowRiskModal] = useState(false);

  // Repository summary state
  const [repositoryInfo, setRepositoryInfo] = useState(null);
  
  // Closed PR count state
  const [closedPRCount, setClosedPRCount] = useState(null);
  const [showHealthCheck, setShowHealthCheck] = useState(false);
  const [showPRSelector, setShowPRSelector] = useState(false);

  // Repository markdown files state
  const [selectedRepositoryFile, setSelectedRepositoryFile] = useState('');
  const [loadingRepoFiles, setLoadingRepoFiles] = useState(false);
  const [availableRepoFiles, setAvailableRepoFiles] = useState([]);

  // Existing GitHub Copilot review issues on the selected PR (shown regardless
  // of whether a rule file exists).
  const [copilotReview, setCopilotReview] = useState(null);
  const [loadingCopilot, setLoadingCopilot] = useState(false);
  // Lyric built-in default rule set suggested when the repo has no rule file.
  const [defaultRules, setDefaultRules] = useState(null);
  const [loadingDefaultRules, setLoadingDefaultRules] = useState(false);
  // The user's saved (editable) rule library + the currently-selected saved rule.
  const [savedRules, setSavedRules] = useState([]);
  const [selectedSavedRuleId, setSelectedSavedRuleId] = useState('');
  // Save/edit-a-rule modal editor state.
  const [ruleEditor, setRuleEditor] = useState(null);
  const [savingRule, setSavingRule] = useState(false);

  const localRulesFileInputRef = useRef(null);

  // The active rule content is a string once a rule file is loaded (repo,
  // upload, Lyric default, or saved). It initializes to [] so guard on the raw
  // value is unreliable — always gate the review on a non-empty string.
  const rulesText = typeof guidelineRules === 'string' ? guidelineRules : '';
  const hasRules = rulesText.trim().length > 0;

  // AUTHENTICATED FETCH HELPER
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    try {
      const token = await authService.getAccessToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      };
      return fetch(url, { ...options, headers });
    } catch (error) {
      console.error('Authentication error:', error);
      throw new Error('Failed to authenticate. Please log in again.');
    }
  }, []);

  // FETCH REPOS
  const fetchRepositories = useCallback(async () => {
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    
    // Check if we have cached data that's still valid
    if (repositoriesCache && repositoriesCacheTime && (now - repositoriesCacheTime) < CACHE_DURATION) {
      console.log('📦 Using cached repositories (5 min cache)');
      setRepositories(repositoriesCache);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const apiUrl = getApiBase();
      const response = await authenticatedFetch(`${apiUrl}/api/github/repos`);
      const data = await response.json();
      if (data.success) {
        const rawRepos = data.repos || data.repositories || [];
        const normalized = rawRepos.map(r => ({
          ...r,
          fullName: r.fullName || r.full_name || r.name || '',
          topics: r.topics || [],
        }));
        setRepositories(normalized);
        setRepositoriesCache(normalized);
        setRepositoriesCacheTime(now);
        console.log('✅ Repositories fetched and cached');
      }
    } catch (err) {
      setError('Error fetching repositories: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [repositoriesCache, repositoriesCacheTime, authenticatedFetch]);

  // PR REPORT GENERATION - Load metadata and last 4 weeks
  const generatePRReport = async (owner, repo, page = 1) => {
    try {
      setIsGeneratingReport(true);
      setError(null);
      const apiUrl = getApiBase();
      
      // Fetch first page to get metadata about all weeks and PRs
      console.log(`🔄 Fetching metadata and first page...`);
      const url = `${apiUrl}/api/github/repos/${owner}/${repo}/pr-report?page=1&perPage=100`;
      const response = await authenticatedFetch(url);
      if (!response.ok) {
        const errData = await response.json();
        // Check for rate limit error
        if (response.status === 403 && errData.message && errData.message.includes('rate limit')) {
          throw new Error('GitHub API rate limit exceeded. The limit resets hourly. Please try again in a few minutes.');
        }
        throw new Error(`Failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      console.log(`✅ PR Report Response received:`, data);
      
      // Extract the categorized PR data from backend response
      const categorizedPRs = data.prs || { clean: [], problematic: [], blocking: [], minor: [], all: [] };
      const categorization = data.categorization || { cleanCount: 0, issuesCount: 0, blockersCount: 0, minorCount: 0 };
      
      // Create report object with categorized structure
      const reportWithCategorization = {
        prs: categorizedPRs,
        categorization: categorization,
        statistics: data.statistics || {}
      };
      
      console.log(`📊 Categorized Report:`, {
        cleanCount: categorization.cleanCount,
        issuesCount: categorization.issuesCount,
        blockersCount: categorization.blockersCount,
        minorCount: categorization.minorCount,
        totalPRs: categorizedPRs.all?.length || 0
      });
      
      setPrReport(reportWithCategorization);
      setPrReportStats(data.statistics || {});
      setRiskSummary(data.riskSummary || null);
      setRepositoryInfo(data.statistics?.repositoryInfo || null);
      setShowPRReport(true);
      
      console.log(`✅ PR Report loaded and displayed`);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // REPO SELECTION HANDLER
  const handleRepositorySelection = async (repoName) => {
    setSelectedRepo(repoName);
    setClosedPRCount(null); // Reset closed PR count
    setPrReport(null); // Clear any existing report
    setShowPRReport(false); // Hide the report display
    setShowHealthCheck(false); // Reset health check
    setShowPRSelector(false); // Reset PR selector
    setPrReportDaysOffset(0); // Reset pagination offset
    setRiskSummary(null);
    setRiskViewMode('summary');
    setShowRiskModal(false);
    setSelectedPR(''); // Clear selected PR
    setPullRequests([]); // Clear PR list
    setSelectedRepositoryFile(''); // Clear selected repo file
    setAvailableRepoFiles([]); // Clear available files list
    
    // Fetch available files from repository's .github directory
    if (repoName) {
      const [owner, repo] = repoName.split('/');
      const apiUrl = getApiBase();
      
      setLoadingRepoFiles(true);
      try {
        const response = await authenticatedFetch(
          `${apiUrl}/api/github/repos/${owner}/${repo}/github-files`
        );
        const data = await response.json();
        
        if (data.success && data.files && Array.isArray(data.files)) {
          setAvailableRepoFiles(data.files);
          console.log(`✅ Loaded ${data.files.length} files from ${repoName}/.github:`, data.files.map(f => f.name));
          if (data.files.length === 0) {
            console.log(`ℹ️ .github directory exists but contains no files`);
          }
        } else {
          setAvailableRepoFiles([]);
          console.log(`ℹ️ No files found in ${repoName}/.github`);
        }
      } catch (error) {
        console.error(`❌ Error fetching available files from ${repoName}:`, error);
        setAvailableRepoFiles([]);
      } finally {
        setLoadingRepoFiles(false);
      }
    }
  };

  // FETCH CLOSED PR COUNT - Single efficient API call
  const fetchClosedPRCount = useCallback(async (owner, repo) => {
    try {
      const apiUrl = getApiBase();
      const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/closed-prs/count`);
      if (!response.ok) {
        throw new Error(`Failed to fetch closed PR count: ${response.statusText}`);
      }
      const data = await response.json();
      setClosedPRCount(data.count || 0);
      console.log(`✅ Total Closed PRs in ${owner}/${repo}: ${data.count}`);
    } catch (err) {
      console.error('Error fetching closed PR count:', err);
      setError(`Failed to fetch closed PR count: ${err.message}`);
    }
  }, [authenticatedFetch]);

  // OPEN SYSTEM EMAIL CLIENT WITH REPORT
  const handleOpenEmailClient = () => {
    try {
      // Build the email report content as plain text
      const reportText = `PR REVIEW ANALYSIS REPORT
========================

Repository: ${selectedRepo}
PR Number: #${selectedPR}
PR Title: ${selectedPRTitle}
Date: ${new Date().toLocaleString()}
Analysis Rules: ${localRulesFile}

VERDICT: ${aiReview.review.verdict}
${aiReview.review.summary}

SCORES
------
Rules Compliance: ${aiReview.scores.rulesCompliance}%
Risk Level: ${aiReview.scores.riskLevel}%
Overall Score: ${aiReview.scores.overallScore}%

STATISTICS
----------
Files Changed: ${aiReview.review.filesChanged}
Additions: +${aiReview.review.additionsCount}
Deletions: -${aiReview.review.deletionsCount}

${aiReview.review.violations?.length > 0 ? `RULE VIOLATIONS (${aiReview.review.violations.length})
` + aiReview.review.violations.map(v => `- ${v.rule}: ${v.message} (${v.severity})`).join('\n') + '\n\n' : ''}

${aiReview.review.issues?.length > 0 ? `ISSUES FOUND (${aiReview.review.issues.length})
` + aiReview.review.issues.map(i => `- ${i.title}: ${i.description}${i.file ? ` (${i.file}${i.line ? ':' + i.line : ''})` : ''}`).join('\n') + '\n\n' : ''}

${aiReview.review.recommendations?.length > 0 ? `RECOMMENDATIONS (${aiReview.review.recommendations.length})
` + aiReview.review.recommendations.map(r => `- ${r.recommendation || r.text || r}`).join('\n') + '\n' : ''}
`;

      // Build mailto URL with the report as body
      const subject = encodeURIComponent(`PR #${selectedPR} Review Analysis Report - ${selectedPRTitle}`);
      const body = encodeURIComponent(reportText);
      const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
      
      console.log('📧 Opening system email client with report');
      // Use window.open with _self fallback — more reliable than location.href
      const mailWindow = window.open(mailtoLink, '_blank');
      if (!mailWindow) {
        window.location.href = mailtoLink;
      }
      
    } catch (err) {
      console.error('Error opening email client:', err);
      setError(`Failed to open email client: ${err.message}`);
    }
  };

  // ---- PR Review tool: Copilot issues + rule sources -----------------------
  const prApiBase = getApiBase();

  // Load existing GitHub Copilot review issues for a PR (unresolved by default).
  const fetchCopilotReview = useCallback(async (prNumber) => {
    if (!selectedRepo || !prNumber) return;
    const [owner, repo] = selectedRepo.split('/');
    setLoadingCopilot(true);
    setCopilotReview(null);
    try {
      const res = await authenticatedFetch(
        `${prApiBase}/api/github/repos/${owner}/${repo}/pulls/${prNumber}/copilot-review`,
      );
      if (!res.ok) {
        setCopilotReview({ error: res.status === 404
          ? 'Copilot review endpoint not available — restart the Lyric server to enable it.'
          : `Request failed (${res.status})` });
        return;
      }
      const data = await res.json();
      setCopilotReview(data.success ? data : { error: data.error || 'Failed to load Copilot review' });
    } catch (err) {
      setCopilotReview({ error: err.message });
    } finally {
      setLoadingCopilot(false);
    }
  }, [selectedRepo, authenticatedFetch, prApiBase]);

  // Suggest a Lyric default rule set for the PR's language (repo has no rule file).
  const fetchDefaultRules = useCallback(async (prNumber) => {
    if (!selectedRepo || !prNumber) return;
    const [owner, repo] = selectedRepo.split('/');
    setLoadingDefaultRules(true);
    try {
      const res = await authenticatedFetch(
        `${prApiBase}/api/github/repos/${owner}/${repo}/pulls/${prNumber}/default-rules`,
      );
      if (!res.ok) return; // Endpoint not available yet (e.g. server not restarted).
      const data = await res.json();
      if (data.success) setDefaultRules(data);
    } catch (err) {
      console.warn('Could not fetch default rules:', err.message);
    } finally {
      setLoadingDefaultRules(false);
    }
  }, [selectedRepo, authenticatedFetch, prApiBase]);

  // Load the user's saved (editable) rule library.
  const fetchSavedRules = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${prApiBase}/api/pr-review/rules`);
      if (!res.ok) return; // Endpoint not available yet (e.g. server not restarted).
      const data = await res.json();
      if (data.success) setSavedRules(data.rules || []);
    } catch (err) {
      console.warn('Could not fetch saved rules:', err.message);
    }
  }, [authenticatedFetch, prApiBase]);

  // Load saved rules only when this tab is active. TabContainer keeps every tab
  // mounted (display:none), so an unguarded mount fetch would fire an
  // authenticated request during the sensitive post-login window and can
  // disrupt session/token establishment — matching the isActive gating used by
  // the other data-loading effects in this component.
  useEffect(() => { if (isActive) fetchSavedRules(); }, [isActive, fetchSavedRules]);

  // Select a PR: also pulls Copilot issues, and (when the repo has no rule file)
  // a Lyric default-rules suggestion.
  const selectPR = (prNumber) => {
    setSelectedPR(prNumber);
    const prObj = pullRequests.find((p) => p.number.toString() === prNumber);
    setSelectedPRTitle(prObj?.title || '');
    setAiReview(null);
    setCopilotReview(null);
    setDefaultRules(null);
    if (prNumber) {
      fetchCopilotReview(prNumber);
      // Only offer Lyric defaults once the repo's .github file list has finished
      // loading and is confirmed empty — otherwise a still-loading (temporarily
      // empty) list would wrongly suggest defaults for a repo that has a rule file.
      if (!loadingRepoFiles && availableRepoFiles.length === 0) fetchDefaultRules(prNumber);
    }
  };

  // Load a Lyric default rule set as the active rule file.
  const useDefaultRules = () => {
    if (!defaultRules) return;
    setGuidelineRules(defaultRules.content);
    setLocalRulesFile(`Lyric default: ${defaultRules.languageLabel}`);
    setSelectedRepositoryFile('');
    setSelectedSavedRuleId('');
    setShowRulesSummary(false);
  };

  // Load a saved rule as the active rule file.
  const loadSavedRule = async (id) => {
    setSelectedSavedRuleId(id);
    if (!id) return;
    try {
      const res = await authenticatedFetch(`${prApiBase}/api/pr-review/rules/${id}`);
      const data = await readJsonResponse(res);
      setGuidelineRules(data.rule.content);
      setLocalRulesFile(`Saved: ${data.rule.name}`);
      setSelectedRepositoryFile('');
      setShowRulesSummary(false);
    } catch (err) {
      setError(`Error loading saved rule: ${err.message}`);
    }
  };

  // Open the editor to save a copy of the current/default rules the user can edit.
  const openSaveCopy = () => {
    const content = rulesText || defaultRules?.content || '';
    const language = defaultRules?.language || '';
    const label = defaultRules?.languageLabel || 'Custom';
    setRuleEditor({
      mode: 'create', id: null, name: `My ${label} rules`,
      language, content, visibility: 'private',
      source: defaultRules ? 'default' : 'custom',
    });
  };

  // Open the editor on an existing saved rule.
  const openEditRule = async (id) => {
    if (!id) return;
    try {
      const res = await authenticatedFetch(`${prApiBase}/api/pr-review/rules/${id}`);
      const data = await readJsonResponse(res);
      setRuleEditor({
        mode: 'edit', id, name: data.rule.name, language: data.rule.language || '',
        content: data.rule.content, visibility: data.rule.visibility, source: data.rule.source,
      });
    } catch (err) {
      setError(`Error loading rule for editing: ${err.message}`);
    }
  };

  // Create or update a saved rule, then load it as the active rule file.
  const saveRule = async () => {
    if (!ruleEditor) return;
    if (!ruleEditor.name.trim() || !ruleEditor.content.trim()) {
      setError('A saved rule needs a name and content.');
      return;
    }
    setSavingRule(true);
    setError(null);
    try {
      const body = {
        name: ruleEditor.name, language: ruleEditor.language,
        content: ruleEditor.content, visibility: ruleEditor.visibility, source: ruleEditor.source,
      };
      const url = ruleEditor.mode === 'edit'
        ? `${prApiBase}/api/pr-review/rules/${ruleEditor.id}`
        : `${prApiBase}/api/pr-review/rules`;
      const res = await authenticatedFetch(url, {
        method: ruleEditor.mode === 'edit' ? 'PATCH' : 'POST',
        body: JSON.stringify(body),
      });
      const data = await readJsonResponse(res);
      await fetchSavedRules();
      setGuidelineRules(data.rule.content);
      setLocalRulesFile(`Saved: ${data.rule.name}`);
      setSelectedSavedRuleId(data.rule.id);
      setSelectedRepositoryFile('');
      setRuleEditor(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingRule(false);
    }
  };

  // Dismiss the rule editor modal with Escape (but never mid-save).
  useEffect(() => {
    if (!ruleEditor) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !savingRule) setRuleEditor(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ruleEditor, savingRule]);

  const handleRulesOnlyReview = async () => {
    if (!hasRules) {
      setError('A rule file is required to run a PR review. Select a repository rule file, upload one, use a Lyric default, or pick a saved rule.');
      return;
    }

    setIsReviewing(true);
    setError(null);
    try {
      const apiUrl = getApiBase();
      const [owner, repo] = selectedRepo.split('/');
      
      console.log(`🔍 Starting PR Review Analysis for ${owner}/${repo}#${selectedPR}`);
      console.log(`📋 Using rules from: ${localRulesFile}`);
      console.log(`📄 Rules content length: ${guidelineRules.length} bytes`);
      
      // Call backend to analyze PR with rules
      const endpoint = `${apiUrl}/api/github/repos/${owner}/${repo}/pulls/${selectedPR}/analyze-with-rules`;
      console.log(`🔄 Sending request to: ${endpoint}`);
      
      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prNumber: parseInt(selectedPR),
          rulesContent: guidelineRules,
          rulesFileName: localRulesFile,
          owner,
          repo
        })
      });

      let analysisResult;
      
      if (!response.ok) {
        // If backend endpoint doesn't exist (404), use enhanced mock data for demonstration
        if (response.status === 404) {
          console.warn(`⚠️ Backend endpoint not yet implemented. Using demonstration mock data.`);
          console.log(`💡 To implement the backend, see: PR_ANALYSIS_BACKEND_SPEC.md`);
          
          // Generate realistic mock data based on PR context
          analysisResult = {
            rulesCompliance: 78,
            riskLevel: 22,
            overallScore: 75,
            summary: `This PR demonstrates a good implementation of the requested features. The code follows most of the specified guidelines with ${guidelineRules.length > 500 ? 'comprehensive' : 'basic'} rule compliance. Some areas for improvement include better error handling and additional test coverage.`,
            verdict: 'CHANGES_REQUESTED',
            issues: [
              {
                title: 'Missing error handling',
                description: 'Several functions lack proper error handling. Consider adding try-catch blocks for async operations.',
                file: 'src/services/api.js',
                line: 45,
                severity: 'high'
              },
              {
                title: 'Incomplete JSDoc comments',
                description: 'Some complex functions missing proper documentation and return type annotations.',
                file: 'src/utils/helpers.js',
                line: 12,
                severity: 'medium'
              }
            ],
            violations: [
              {
                rule: 'Error Handling',
                message: 'Function fetchUserData() should have try-catch block',
                severity: 'high',
                file: 'src/services/api.js'
              },
              {
                rule: 'Code Documentation',
                message: 'Missing JSDoc for exported utility functions',
                severity: 'medium',
                file: 'src/utils/helpers.js'
              }
            ],
            recommendations: [
              'Add comprehensive error handling for all async functions',
              'Include JSDoc comments for all exported functions',
              'Add unit tests for new utility functions',
              'Consider using TypeScript for better type safety'
            ],
            changedFiles: ['src/services/api.js', 'src/utils/helpers.js', 'src/components/Dashboard.jsx', 'tests/api.test.js'],
            filesChanged: 4,
            additionsCount: 245,
            deletionsCount: 87
          };
          console.log(`✅ Using demonstration mock data (backend not yet implemented)`);
        } else {
          const errorData = await response.text();
          console.error(`❌ Response status: ${response.status}`);
          console.error(`❌ Response body: ${errorData}`);
          throw new Error(`Analysis failed: ${response.status} - ${errorData || response.statusText}`);
        }
      } else {
        analysisResult = await response.json();
        console.log(`✅ Analysis Result from backend:`, analysisResult);
      }

      // Structure the review result for display
      const structuredReview = {
        scores: {
          rulesCompliance: analysisResult.rulesCompliance || 0,
          riskLevel: analysisResult.riskLevel || 0,
          overallScore: analysisResult.overallScore || 0
        },
        review: {
          summary: analysisResult.summary || 'Analysis complete.',
          issues: analysisResult.issues || [],
          violations: analysisResult.violations || [],
          recommendations: analysisResult.recommendations || [],
          verdict: analysisResult.verdict || 'REVIEW_NEEDED',
          changedFiles: analysisResult.changedFiles || [],
          additionsCount: analysisResult.additionsCount || 0,
          deletionsCount: analysisResult.deletionsCount || 0,
          filesChanged: analysisResult.filesChanged || 0
        }
      };

      setAiReview(structuredReview);
      console.log(`✅ PR Review completed successfully`);
      
    } catch (err) {
      console.error('Error during PR review:', err);
      setError(`PR Review failed: ${err.message}`);
      setAiReview(null);
    } finally {
      setIsReviewing(false);
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchRepositories();
      // Check for deep link ?repo= query param first
      const urlParams = new URLSearchParams(window.location.search);
      const repoParam = urlParams.get('repo');
      if (repoParam) {
        setSelectedRepo(repoParam);
        // Clean the URL params after consuming them
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } else {
        // Restore last selected repository from localStorage
        const lastSelectedRepo = localStorage.getItem('lastSelectedRepo');
        if (lastSelectedRepo) {
          setSelectedRepo(lastSelectedRepo);
        }
      }
    }
  }, [isActive, fetchRepositories]);

  // Save selected repository to localStorage whenever it changes
  useEffect(() => {
    if (selectedRepo) {
      localStorage.setItem('lastSelectedRepo', selectedRepo);
      // Load data for the selected repo
      const [owner, repo] = selectedRepo.split('/');
      fetchClosedPRCount(owner, repo);
      
      // Load open PRs for the PR selection dropdown
      (async () => {
        setLoadingPRs(true);
        setPrLoadError(null);
        try {
          const apiUrl = getApiBase();
          const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/pulls?state=open`);
          const rawBody = await response.text();
          let data;
          try {
            data = JSON.parse(rawBody);
          } catch {
            throw new Error(
              `Server returned a non-JSON response (HTTP ${response.status}). ` +
              `If you just updated Lyric, restart the server.`,
            );
          }
          if (!response.ok || data.success === false) {
            throw new Error(data.error || `Failed to load pull requests (HTTP ${response.status})`);
          }
          setPullRequests(data.pulls || []);
        } catch (err) {
          console.error('Error loading PRs:', err);
          setPullRequests([]);
          setPrLoadError(err.message);
        } finally {
          setLoadingPRs(false);
        }
      })();
    }
  }, [selectedRepo, fetchClosedPRCount, authenticatedFetch]);

  return (
    <div style={styles.form}>
      <h2 style={{ color: '#000000', fontWeight: 'bold', marginBottom: '20px' }}>
        🔀 Code Ai
      </h2>

      {/* Code AI Sub-Tab Switcher */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e9ecef', paddingBottom: '10px' }}>
        <button
          onClick={() => setCodeAiSubTab('pr-analysis')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: codeAiSubTab === 'pr-analysis' ? '#667eea' : '#f0f0f0',
            color: codeAiSubTab === 'pr-analysis' ? '#fff' : '#000',
            fontWeight: codeAiSubTab === 'pr-analysis' ? 'bold' : 'normal',
            transition: 'all 0.2s'
          }}
        >
          📊 Pull Request Analysis
        </button>
        <button
          onClick={() => setCodeAiSubTab('bug-hunt')}
          disabled={loading}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            backgroundColor: loading ? '#ccc' : (codeAiSubTab === 'bug-hunt' ? '#667eea' : '#f0f0f0'),
            color: codeAiSubTab === 'bug-hunt' && !loading ? '#fff' : '#000',
            fontWeight: codeAiSubTab === 'bug-hunt' ? 'bold' : 'normal',
            transition: 'all 0.2s',
            opacity: loading ? 0.6 : 1
          }}
        >
          🔎 Bug Hunt
        </button>
        <button
          onClick={() => setCodeAiSubTab('branch-review')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: codeAiSubTab === 'branch-review' ? '#667eea' : '#f0f0f0',
            color: codeAiSubTab === 'branch-review' ? '#fff' : '#000',
            fontWeight: codeAiSubTab === 'branch-review' ? 'bold' : 'normal',
            transition: 'all 0.2s'
          }}
        >
          🌿 Branch Review
        </button>
        <button
          onClick={() => setCodeAiSubTab('file-analysis')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: codeAiSubTab === 'file-analysis' ? '#667eea' : '#f0f0f0',
            color: codeAiSubTab === 'file-analysis' ? '#fff' : '#000',
            fontWeight: codeAiSubTab === 'file-analysis' ? 'bold' : 'normal',
            transition: 'all 0.2s'
          }}
        >
          📄 File Analysis
        </button>
      </div>

      {/* PR Analysis Tab Content */}
      {codeAiSubTab === 'pr-analysis' && (
        <>
          <h3 style={{ color: '#000000', fontWeight: 'bold', marginBottom: '15px' }}>
            Pull Request Analysis
          </h3>

      {/* Rate Limit Notice - Only show if rate limit error occurred */}
      {error && error.includes('rate limit') && (
        <div style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', marginBottom: '15px', fontSize: '13px', color: '#856404' }}>
          <strong>⏱️ Rate Limit Info:</strong> GitHub API has an hourly rate limit. If you see "API rate limit exceeded" error, please wait 1 hour for it to reset. The app uses smart caching and pagination to minimize API calls.
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {/* 1. SELECT REPOSITORY */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={styles.label}>1️⃣ Select Repository</label>
          {selectedRepo && (
            <button 
              onClick={() => {
                // Clear entire screen and reset all state
                setSelectedRepo('');
                setRepoSearch('');
                setClosedPRCount(null);
                setPrReport(null);
                setShowPRReport(false);
                setShowHealthCheck(false);
                setShowPRSelector(false);
                setPrReportDaysOffset(0);
                setSelectedPR('');
                setSelectedPRTitle('');
                setPullRequests([]);
                setSelectedRepositoryFile('');
                setGuidelineRules([]);
                setLocalRulesFile(null);
                setAiReview(null);
                setPrFilter('all');
                setError(null);
              }} 
              style={{ padding: '6px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
            >
              Reset
            </button>
          )}
        </div>
        <input
          type="text"
          placeholder="Search repositories..."
          value={repoSearch}
          onChange={(e) => setRepoSearch(e.target.value)}
          style={styles.searchInput}
        />
        {loading && <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>📥 Loading repositories...</p>}
        <select
          value={selectedRepo}
          onChange={(e) => handleRepositorySelection(e.target.value)}
          style={styles.select}
          disabled={loading || repositories.length === 0}
        >
          <option value="">{loading ? 'Loading...' : repositories.length > 0 ? 'Choose a repository...' : 'No repositories found'}</option>
          {repositories.filter(r => r.fullName.toLowerCase().includes(repoSearch.toLowerCase())).map(repo => (
            <option key={repo.id} value={repo.fullName}>{repo.fullName}</option>
          ))}
        </select>

        {selectedRepo && (
          <div style={{ marginTop: '12px' }}>
            {/* Action Buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button
                onClick={() => {
                  const [owner, repo] = selectedRepo.split('/');
                  generatePRReport(owner, repo);
                  setShowHealthCheck(true);
                  setShowPRSelector(false);
                }}
                disabled={isGeneratingReport || loading}
                style={{ 
                  ...styles.button, 
                  backgroundColor: (isGeneratingReport || loading) ? '#6c757d' : '#17a2b8',
                  cursor: (isGeneratingReport || loading) ? 'not-allowed' : 'pointer',
                  opacity: (isGeneratingReport || loading) ? 0.6 : 1
                }}
              >
                {isGeneratingReport ? '⏳ Checking...' : loading ? '⏳ Loading...' : '🏥 Check Repository Health'}
              </button>
              <button
                onClick={() => {
                  setShowHealthCheck(false);
                  setShowPRReport(false);
                  setShowPRSelector(true);
                  setPrReport(null); // Clear any health report
                  setAiReview(null); // Clear any previous review
                }}
                disabled={loading}
                style={{ 
                  ...styles.button, 
                  backgroundColor: loading ? '#6c757d' : '#667eea',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? '⏳ Loading...' : '🔍 Run PR Review'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 2. PR SELECTION (Only show if "Run PR Review" button was clicked) */}
      {showPRSelector && (
        <div style={styles.section}>
          <label style={styles.label}>2️⃣ Select Pull Request</label>
          {loadingPRs && (
            <p style={{ fontSize: '13px', color: '#667eea', margin: '6px 0' }}>
              ⏳ Loading open pull requests…
            </p>
          )}
          {!loadingPRs && prLoadError && (
            <p style={{ fontSize: '13px', color: '#dc3545', margin: '6px 0' }}>
              ⚠️ Could not load pull requests: {prLoadError}
            </p>
          )}
          <select
            value={selectedPR}
            onChange={(e) => selectPR(e.target.value)}
            style={styles.select}
            disabled={loadingPRs}
          >
            <option value="">
              {loadingPRs ? 'Loading…' : 'Choose an open PR...'}
            </option>
            {pullRequests.map(pr => (
              <option key={pr.id} value={pr.number}>#{pr.number} - {pr.title}</option>
            ))}
          </select>
          {!loadingPRs && !prLoadError && pullRequests.length === 0 && (
            <p style={{ fontSize: '13px', color: '#666', margin: '6px 0' }}>
              No open pull requests found for this repository.
            </p>
          )}
        </div>
      )}

      {/* 3. ACTION BUTTONS - Show after PR is selected */}
      {selectedPR && showPRSelector && !showPRReport && (
        <div style={styles.section}>
          <label style={styles.label}>3️⃣ Analysis Actions</label>
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
            Select an action to analyze PR #{selectedPR}
          </p>

          {/* GitHub Copilot existing review issues (shown regardless of rules) */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{ ...styles.label, marginBottom: '8px' }}>🤖 GitHub Copilot Review Issues</label>
            {loadingCopilot && (
              <p style={{ fontSize: '12px', color: '#667eea', margin: '6px 0' }}>⏳ Checking for Copilot review issues…</p>
            )}
            {!loadingCopilot && copilotReview?.error && (
              <p style={{ fontSize: '12px', color: '#dc3545', margin: '6px 0' }}>
                ⚠️ Could not load Copilot issues: {copilotReview.error}
              </p>
            )}
            {!loadingCopilot && copilotReview && !copilotReview.error && !copilotReview.hasReviewed && (
              <p style={{ fontSize: '12px', color: '#999', margin: '6px 0' }}>
                ℹ️ GitHub Copilot has not reviewed this PR.
              </p>
            )}
            {!loadingCopilot && copilotReview && !copilotReview.error && copilotReview.hasReviewed && (
              <div style={{
                padding: '12px', backgroundColor: '#fff', border: '1px solid #ddd',
                borderRadius: '6px', fontSize: '13px', color: '#000',
              }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: '12px', backgroundColor: '#f8d7da', color: '#842029', fontWeight: 'bold' }}>
                    {copilotReview.unresolvedCount} unresolved
                  </span>
                  <span style={{ padding: '2px 10px', borderRadius: '12px', backgroundColor: '#d1e7dd', color: '#0f5132' }}>
                    {copilotReview.resolvedCount} resolved
                  </span>
                  {copilotReview.reviewedAt && (
                    <span style={{ color: '#666', alignSelf: 'center' }}>
                      Reviewed {new Date(copilotReview.reviewedAt).toLocaleString()}
                    </span>
                  )}
                </div>
                {copilotReview.summary && (
                  <div style={{
                    marginBottom: '10px', padding: '8px', backgroundColor: '#f8f9fa',
                    borderRadius: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#333',
                  }}>
                    {copilotReview.summary}
                  </div>
                )}
                {copilotReview.unresolved?.length > 0 ? (
                  <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    {copilotReview.unresolved.map((issue, idx) => (
                      <div key={idx} style={{
                        padding: '8px 0', borderTop: idx === 0 ? 'none' : '1px solid #eee',
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          📄 {issue.path}{issue.line ? `:${issue.line}` : ''}
                          {issue.isOutdated && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#856404', backgroundColor: '#fff3cd', padding: '1px 6px', borderRadius: '8px' }}>outdated</span>
                          )}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#333', marginBottom: '4px' }}>
                          {issue.body}
                        </div>
                        {issue.url && (
                          <a href={issue.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#667eea', textDecoration: 'underline' }}>
                            View on GitHub →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: '12px', color: '#0f5132', margin: '4px 0' }}>
                    ✅ No unresolved Copilot issues on this PR.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Repository Rules File Selection */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ ...styles.label, marginBottom: '8px' }}>Select Rules from Repository (.github directory)</label>
            <select
              onChange={(e) => {
                const filePath = e.target.value;
                if (filePath) {
                  setSelectedRepositoryFile(filePath);
                  setLocalRulesFile(''); // Clear local file if repo file is selected
                  
                  // Fetch the file from the repository
                  const [owner, repo] = selectedRepo.split('/');
                  const apiUrl = getApiBase();
                  
                  setLoadingRepoFiles(true);
                  authenticatedFetch(
                    `${apiUrl}/api/github/repos/${owner}/${repo}/file-content?filePath=${encodeURIComponent(filePath)}`
                  )
                    .then(res => res.json())
                    .then(data => {
                      if (data.success) {
                        setGuidelineRules(data.content);
                        setShowRulesSummary(false);
                        console.log(`✅ Loaded rules from repository: ${filePath}`);
                      } else {
                        setError(`Failed to load rules file: ${data.error || 'Unknown error'}`);
                      }
                    })
                    .catch(err => {
                      setError(`Error loading rules file: ${err.message}`);
                    })
                    .finally(() => {
                      setLoadingRepoFiles(false);
                    });
                }
              }}
              disabled={loadingRepoFiles || !selectedRepo || availableRepoFiles.length === 0}
              style={{ ...styles.select }}
            >
              <option value="">
                {!selectedRepo ? 'Select a repository first...' : 
                 loadingRepoFiles ? 'Loading files...' :
                 availableRepoFiles.length === 0 ? 'No files found in .github directory' :
                 'Choose rules file from repository...'}
              </option>
              {availableRepoFiles.map((file) => (
                <option key={file.path} value={file.path}>
                  {file.name} {file.size ? `(${(file.size / 1024).toFixed(1)}KB)` : ''}
                </option>
              ))}
            </select>
            {selectedRepositoryFile && !loadingRepoFiles && (
              <p style={{ fontSize: '12px', color: '#28a745', marginTop: '6px', marginBottom: '8px' }}>
                ✅ Rules loaded from: {selectedRepositoryFile}
              </p>
            )}
            {loadingRepoFiles && (
              <p style={{ fontSize: '12px', color: '#667eea', marginTop: '6px', marginBottom: '8px' }}>
                ⏳ Loading rules file...
              </p>
            )}
            {!loadingRepoFiles && selectedRepo && availableRepoFiles.length === 0 && (
              <p style={{ fontSize: '12px', color: '#999', marginTop: '6px', marginBottom: '8px' }}>
                ℹ️ The .github directory exists but has no files. You can use a Lyric default rule set, pick a saved rule, or upload a local rules file below.
              </p>
            )}
          </div>

          {/* Lyric default rule set offer (when the repo has no rule file) */}
          {availableRepoFiles.length === 0 && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ ...styles.label, marginBottom: '8px' }}>Use a Lyric Default Rule Set</label>
              {loadingDefaultRules && (
                <p style={{ fontSize: '12px', color: '#667eea', margin: '6px 0' }}>⏳ Detecting language…</p>
              )}
              {!loadingDefaultRules && defaultRules && (
                <div style={{
                  padding: '12px', backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff',
                  borderRadius: '6px', fontSize: '13px', color: '#004085',
                }}>
                  <p style={{ margin: '0 0 10px 0' }}>
                    No rule file in this repository — but you can use Lyric's default{' '}
                    <strong>{defaultRules.languageLabel}</strong> rules for this type of code.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={useDefaultRules}
                      style={{ ...styles.button, backgroundColor: '#17a2b8', cursor: 'pointer', padding: '8px 14px' }}
                    >
                      ✅ Use {defaultRules.languageLabel} defaults
                    </button>
                    <button
                      onClick={openSaveCopy}
                      style={{ ...styles.button, backgroundColor: '#6f42c1', cursor: 'pointer', padding: '8px 14px' }}
                    >
                      💾 Save a copy I can edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Saved (editable) rule library */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ ...styles.label, marginBottom: '8px' }}>Your Saved Rules</label>
            <select
              value={selectedSavedRuleId}
              onChange={(e) => loadSavedRule(e.target.value)}
              style={{ ...styles.select }}
            >
              <option value="">
                {savedRules.length === 0 ? 'No saved rules yet' : 'Choose a saved rule…'}
              </option>
              {savedRules.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.language ? ` (${r.language})` : ''}{r.visibility === 'shared' ? ' — shared' : ''}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
              {selectedSavedRuleId && savedRules.find((r) => r.id === selectedSavedRuleId)?.canEdit && (
                <button
                  onClick={() => openEditRule(selectedSavedRuleId)}
                  style={{ ...styles.button, backgroundColor: '#6f42c1', cursor: 'pointer', padding: '8px 14px' }}
                >
                  ✏️ Edit selected rule
                </button>
              )}
              {hasRules && (
                <button
                  onClick={openSaveCopy}
                  style={{ ...styles.button, backgroundColor: '#6f42c1', cursor: 'pointer', padding: '8px 14px' }}
                >
                  💾 Save current rules as a copy
                </button>
              )}
            </div>
          </div>

          {/* Local Rules File Selection */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ ...styles.label, marginBottom: '8px' }}>Upload Local Rules File</label>
            <input
              type="file"
              ref={localRulesFileInputRef}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    try {
                      const content = event.target.result;
                      // Store raw file content (can be plain text, markdown, JSON, etc.)
                      setGuidelineRules(content);
                      setLocalRulesFile(file.name);
                      setSelectedRepositoryFile(''); // Clear repo selection if local file is uploaded
                      setShowRulesSummary(false); // Reset dropdown when new file is loaded
                      console.log(`✅ Loaded file: ${file.name} (${content.length} bytes)`);
                    } catch (err) {
                      setError('Failed to read file. Please try again.');
                    }
                  };
                  reader.readAsText(file);
                }
              }}
              style={{ ...styles.select, padding: '8px' }}
            />
            {localRulesFile && selectedRepositoryFile === '' && (
              <div>
                <p style={{ fontSize: '12px', color: '#28a745', marginTop: '6px', marginBottom: '8px' }}>
                  ✅ Rules file loaded: {localRulesFile}
                </p>
                {/* Rules Summary Dropdown */}
                <button
                  onClick={() => setShowRulesSummary(!showRulesSummary)}
                  style={{
                    ...styles.button,
                    width: '100%',
                    backgroundColor: '#f0f0f0',
                    color: '#333',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    fontSize: '13px',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>📋 View Rules Summary</span>
                  <span style={{ fontSize: '16px', transform: showRulesSummary ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>▶</span>
                </button>
                
                {/* Rules Summary Content */}
                {showRulesSummary && guidelineRules && (
                  <div style={{
                    marginTop: '10px',
                    padding: '15px',
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    maxHeight: '500px',
                    overflowY: 'auto',
                    fontFamily: 'inherit',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    color: '#000',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {guidelineRules}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Action 1: Run PR Review */}
          <button 
            onClick={handleRulesOnlyReview} 
            disabled={isReviewing || !hasRules}
            style={{ 
              ...styles.button, 
              width: '100%', 
              backgroundColor: (isReviewing || !hasRules) ? '#6c757d' : '#28a745',
              cursor: (isReviewing || !hasRules) ? 'not-allowed' : 'pointer',
              opacity: (isReviewing || !hasRules) ? 0.6 : 1,
              marginBottom: '10px'
            }}
          >
            {isReviewing ? '⏳ Analyzing...' : '🔍 Run PR Review'}
          </button>
          {!hasRules ? (
            <p style={{ fontSize: '12px', color: '#dc3545', marginLeft: '0px' }}>
              🔒 A rule file is required to run a PR review. Select a repository rule file, use a Lyric default, pick a saved rule, or upload one above.
            </p>
          ) : (
            <p style={{ fontSize: '12px', color: '#666', marginLeft: '0px' }}>
              Analyzes this PR against recent closed PR patterns and guidelines
            </p>
          )}
        </div>
      )}

      {/* Rule editor modal — save a copy / edit a saved rule */}
      {ruleEditor && (
        <div
          onClick={() => !savingRule && setRuleEditor(null)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rule-editor-title"
            style={{
              backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
              width: 'min(760px, 92vw)', maxHeight: '88vh', overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)', color: '#000',
            }}
          >
            <h3 id="rule-editor-title" style={{ marginTop: 0, marginBottom: '16px' }}>
              {ruleEditor.mode === 'edit' ? '✏️ Edit Saved Rule' : '💾 Save a Rule Copy'}
            </h3>
            <label style={{ ...styles.label, marginBottom: '6px' }}>Name</label>
            <input
              type="text"
              value={ruleEditor.name}
              onChange={(e) => setRuleEditor({ ...ruleEditor, name: e.target.value })}
              style={{ ...styles.select, marginBottom: '12px' }}
            />
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ ...styles.label, marginBottom: '6px' }}>Language (optional)</label>
                <input
                  type="text"
                  value={ruleEditor.language}
                  onChange={(e) => setRuleEditor({ ...ruleEditor, language: e.target.value })}
                  placeholder="e.g. typescript"
                  style={{ ...styles.select }}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ ...styles.label, marginBottom: '6px' }}>Visibility</label>
                <select
                  value={ruleEditor.visibility}
                  onChange={(e) => setRuleEditor({ ...ruleEditor, visibility: e.target.value })}
                  style={{ ...styles.select }}
                >
                  <option value="private">Private (only me)</option>
                  <option value="shared">Shared (all Lyric users)</option>
                </select>
              </div>
            </div>
            <label style={{ ...styles.label, marginBottom: '6px' }}>Rule content</label>
            <textarea
              value={ruleEditor.content}
              onChange={(e) => setRuleEditor({ ...ruleEditor, content: e.target.value })}
              style={{
                width: '100%', minHeight: '320px', fontFamily: 'monospace', fontSize: '13px',
                padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box',
                whiteSpace: 'pre', overflowWrap: 'normal', color: '#000',
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={() => setRuleEditor(null)}
                disabled={savingRule}
                style={{ ...styles.button, backgroundColor: '#6c757d', cursor: savingRule ? 'not-allowed' : 'pointer', padding: '10px 18px' }}
              >
                Cancel
              </button>
              <button
                onClick={saveRule}
                disabled={savingRule}
                style={{ ...styles.button, backgroundColor: savingRule ? '#6c757d' : '#28a745', cursor: savingRule ? 'not-allowed' : 'pointer', padding: '10px 18px' }}
              >
                {savingRule ? '⏳ Saving…' : '💾 Save & Use'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PR REPORT DISPLAY - Show when "Check Repository Health" button is clicked */}
      {showHealthCheck && prReport && (
        <div style={{ marginTop: '30px', padding: '20px', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#fff', fontSize: '14px', fontFamily: 'inherit', color: '#000' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', color: '#000' }}>📊 Repository Health Check: {selectedRepo}</h3>
            <button onClick={() => setShowHealthCheck(false)} style={{ padding: '5px 15px', cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit', color: '#000', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>Close</button>
          </div>

          {/* Closed PR Count Display */}
          <div style={{ ...styles.scoreCard, marginBottom: '20px', textAlign: 'center', backgroundColor: '#f0f8ff', border: '2px solid #17a2b8' }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#17a2b8' }}>{closedPRCount?.toLocaleString()}</div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Total Closed Pull Requests</div>
            </div>
          </div>

          {/* Repository Info Summary */}
          {repositoryInfo && (
            <div style={{ padding: '12px', backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff', borderRadius: '4px', marginBottom: '20px', fontSize: '13px', color: '#004085' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>📦 Repository:</strong> <a href={repositoryInfo.url} target="_blank" rel="noopener noreferrer" style={{ color: '#004085', textDecoration: 'underline' }}>{repositoryInfo.name}</a>
              </div>
              {repositoryInfo.description && (
                <div style={{ marginBottom: '4px' }}>
                  <strong>ℹ️ Description:</strong> {repositoryInfo.description}
                </div>
              )}
              <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#495057' }}>
                💡 {repositoryInfo.note}
              </div>
            </div>
          )}

          {/* RISK ANALYSIS TOGGLE BUTTONS */}
          {riskSummary && (
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => setRiskViewMode('summary')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: riskViewMode === 'summary' ? '#17a2b8' : '#e9ecef',
                  color: riskViewMode === 'summary' ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px',
                  transition: 'all 0.3s'
                }}
              >
                📊 Risk Summary
              </button>
              <button
                onClick={() => setRiskViewMode('detailed')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: riskViewMode === 'detailed' ? '#17a2b8' : '#e9ecef',
                  color: riskViewMode === 'detailed' ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px',
                  transition: 'all 0.3s'
                }}
              >
                📋 PR List
              </button>
              <button 
                onClick={async () => {
                  if (isDownloadingPDF) return; // Prevent multiple clicks
                  setIsDownloadingPDF(true);
                  setError('');
                  try {
                    // Generate PDF report and send via email
                    const [owner, repo] = selectedRepo.split('/');
                    const githubUrl = `https://github.com/${owner}/${repo}`;
                    const lyricBaseUrl = process.env.REACT_APP_LYRIC_URL || 'https://lyric.dev.rapidai.com';
                    const lyricReportLink = `${lyricBaseUrl}/?tab=pullrequest&repo=${encodeURIComponent(selectedRepo)}`;
                    
                    // Count risk levels
                    const lowRiskCount = prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'low')?.length || 0;
                    const mediumRiskCount = prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'medium')?.length || 0;
                    const highRiskCount = prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'high')?.length || 0;
                    const criticalRiskCount = prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'critical')?.length || 0;
                    
                    // Prepare report data for backend
                    const reportData = {
                      repository: selectedRepo,
                      githubUrl: githubUrl,
                      lyricReportLink: lyricReportLink,
                      timestamp: new Date().toISOString(),
                      summary: {
                        lowRisk: lowRiskCount,
                        mediumRisk: mediumRiskCount,
                        highRisk: highRiskCount,
                        criticalRisk: criticalRiskCount,
                        totalPRs: prReport?.prs?.all?.length || 0,
                        avgRiskScore: riskSummary?.avgRiskScore || 'N/A'
                      },
                      prs: prReport?.prs?.all || []
                    };
                    
                    // Call backend to generate PDF and get email template
                    const apiUrl = getApiBase();
                    const token = await authService.getAccessToken();
                    
                    setError('⏳ Generating PDF report...');
                    const response = await fetch(`${apiUrl}/api/generate-pr-report-email`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify(reportData)
                    });
                    
                    if (!response.ok) {
                      // Surface the server's actual error (e.g. the specific
                      // puppeteer/Chromium failure) instead of a generic string.
                      let serverMsg = `HTTP ${response.status}`;
                      try {
                        const errBody = await response.json();
                        if (errBody?.error) serverMsg = errBody.error;
                      } catch { /* response wasn't JSON */ }
                      throw new Error(`Failed to generate PDF report: ${serverMsg}`);
                    }
                    
                    const { htmlEmail, subject, pdfFileName } = await response.json();
                    
                    // Download the PDF file using fetch (to send Authorization header)
                    if (pdfFileName) {
                      const downloadUrl = `${apiUrl}/api/download-pr-report/${pdfFileName}`;
                      try {
                        setError('⏳ Downloading PDF...');
                        const downloadResponse = await fetch(downloadUrl, {
                          method: 'GET',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        
                        if (!downloadResponse.ok) {
                          const errorData = await downloadResponse.json();
                          throw new Error(errorData.error || 'Failed to download PDF');
                        }
                        
                        const blob = await downloadResponse.blob();
                        const blobUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = pdfFileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                        
                        // Show success message briefly
                        setError(`✅ PDF downloaded! Opening email client...`);
                        
                        // Build plain-text email body with report summary
                        const repoName = selectedRepo;
                        const totalPRs = prReport?.prs?.all?.length || 0;
                        const emailBody = [
                          `Hi,`,
                          ``,
                          `Please find attached the AI Code Review Report for ${repoName}.`,
                          ``,
                          `Report Summary:`,
                          `  • Repository: ${repoName}`,
                          `  • Total PRs Analyzed: ${totalPRs}`,
                          `  • Risk Breakdown:`,
                          `      - Critical: ${criticalRiskCount}`,
                          `      - High: ${highRiskCount}`,
                          `      - Medium: ${mediumRiskCount}`,
                          `      - Low: ${lowRiskCount}`,
                          `  • Average Risk Score: ${riskSummary?.avgRiskScore || 'N/A'}`,
                          ``,
                          `View full interactive report: ${lyricReportLink}`,
                          ``,
                          `Best regards`
                        ].join('\n');
                        
                        // Open default email client with subject + body
                        const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject || `AI Code Review Report - ${repoName}`)}&body=${encodeURIComponent(emailBody)}`;
                        const mailWin = window.open(mailtoUrl, '_blank');
                        if (!mailWin) {
                          window.location.href = mailtoUrl;
                        }
                        
                        setTimeout(() => setError(''), 8000);
                      } catch (downloadErr) {
                        console.error('Error downloading PDF:', downloadErr);
                        setError(`Failed to download PDF: ${downloadErr.message}`);
                      }
                    } else {
                      setError('PDF file was not generated');
                    }
                    
                  } catch (err) {
                    console.error('Error generating email report:', err);
                    setError(`Failed to generate email report: ${err.message}`);
                  } finally {
                    setIsDownloadingPDF(false);
                  }
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isDownloadingPDF ? '#6c757d' : '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isDownloadingPDF ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px',
                  transition: 'all 0.3s',
                  opacity: isDownloadingPDF ? 0.8 : 1
                }}
                disabled={isDownloadingPDF}
              >
                {isDownloadingPDF ? '⏳ Loading...' : '⬇️ Download'}
              </button>
            </div>
          )}

          {/* RISK SUMMARY DASHBOARD */}
          {riskSummary && riskViewMode === 'summary' && (
            <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8f9fa', border: '2px solid #17a2b8', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#17a2b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🎯 Risk Analysis Summary
              </h4>

              {/* Risk Distribution Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #28a745', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{riskSummary.lowRisks}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🟢 Low</div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #ffc107', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>{riskSummary.mediumRisks}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🟡 Medium</div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #fd7e14', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14' }}>{riskSummary.highRisks}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🟠 High</div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #dc3545', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>{riskSummary.criticalRisks}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🔴 Critical</div>
                </div>
              </div>

              {/* Average Risk Score */}
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <strong style={{ color: '#333' }}>Average Risk Score:</strong>
                  <span style={{ fontSize: '18px', fontWeight: 'bold', color: riskSummary.avgRiskScore > 60 ? '#dc3545' : (riskSummary.avgRiskScore > 40 ? '#fd7e14' : '#28a745') }}>
                    {riskSummary.avgRiskScore}/100
                  </span>
                </div>
                <div style={{ width: '100%', height: '16px', backgroundColor: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${riskSummary.avgRiskScore}%`,
                      height: '100%',
                      backgroundColor: riskSummary.avgRiskScore > 60 ? '#dc3545' : (riskSummary.avgRiskScore > 40 ? '#fd7e14' : '#28a745'),
                      transition: 'width 0.3s'
                    }}
                  />
                </div>
              </div>

              {/* Most Common Risks */}
              {Object.keys(riskSummary.mostCommonRisks).length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#333', fontWeight: 'bold' }}>⚠️ Most Common Risks:</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(riskSummary.mostCommonRisks).slice(0, 5).map(([risk, count], idx) => (
                      <div key={idx} style={{ padding: '10px', backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#333' }}>{risk.substring(0, 40)}</span>
                        <span style={{ fontSize: '11px', fontWeight: 'bold', backgroundColor: '#e9ecef', padding: '3px 8px', borderRadius: '12px', color: '#666' }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {riskSummary.recommendations.length > 0 && (
                <div>
                  <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#333', fontWeight: 'bold' }}>💡 Recommendations:</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {riskSummary.recommendations.slice(0, 3).map((rec, idx) => (
                      <div key={idx} style={{ padding: '10px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '12px', color: '#1565c0', lineHeight: '1.4' }}>
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PR LIST WITH RISK BADGES (when detailed view) */}
          {riskViewMode === 'detailed' && (
            <>

          {/* Date Range Navigation Controls */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
              <button
                onClick={async () => {
                  // Move back 10 days
                  setIsLoadingPRReport(true);
                  try {
                    const [owner, repo] = selectedRepo.split('/');
                    const newOffset = prReportDaysOffset + 10;
                    const endDate = new Date();
                    endDate.setDate(endDate.getDate() - newOffset);
                    endDate.setHours(0, 0, 0, 0);
                    const endDateStr = endDate.toISOString().split('T')[0];
                    
                    const startDate = new Date(endDate);
                    startDate.setDate(startDate.getDate() - 10);
                    startDate.setHours(0, 0, 0, 0);
                    const startDateStr = startDate.toISOString().split('T')[0];
                    
                    const apiUrl = getApiBase();
                    const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/pr-report?startDate=${startDateStr}&endDate=${endDateStr}`);
                    
                    if (!response.ok) {
                      throw new Error(`Failed to fetch PR data: ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    const categorizedPRs = data.prs || { clean: [], problematic: [], blocking: [], minor: [], all: [] };
                    const categorization = data.categorization || { cleanCount: 0, issuesCount: 0, blockersCount: 0, minorCount: 0 };
                    
                    const reportWithCategorization = {
                      prs: categorizedPRs,
                      categorization: categorization,
                      statistics: data.statistics || {}
                    };
                    
                    setPrReport(reportWithCategorization);
                    setPrReportStats(data.statistics || {});
                    setPrReportDaysOffset(newOffset);
                    setPrFilter('all');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } catch (err) {
                    setError(`Error loading previous period: ${err.message}`);
                  } finally {
                    setIsLoadingPRReport(false);
                  }
                }}
                disabled={prReportDaysOffset >= 100 || isLoadingPRReport}
                style={{
                  padding: '8px 12px',
                  backgroundColor: isLoadingPRReport ? '#FFB300' : (prReportDaysOffset >= 100 ? '#ccc' : '#667eea'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoadingPRReport || prReportDaysOffset >= 100 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: isLoadingPRReport ? 1 : (prReportDaysOffset >= 100 ? 0.6 : 1),
                  transition: 'all 0.2s'
                }}
              >
                {isLoadingPRReport ? '⏳ Loading...' : '← Previous 10 Days'}
              </button>
              
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', minWidth: '200px', textAlign: 'center' }}>
                {(() => {
                  const endDate = new Date();
                  endDate.setDate(endDate.getDate() - prReportDaysOffset);
                  const startDate = new Date(endDate);
                  startDate.setDate(startDate.getDate() - 10);
                  const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `📅 ${monthsShort[startDate.getMonth()]} ${startDate.getDate()} - ${monthsShort[endDate.getMonth()]} ${endDate.getDate()}, ${endDate.getFullYear()}`;
                })()}
              </div>
              
              <button
                onClick={async () => {
                  // Move forward 10 days
                  if (prReportDaysOffset <= 0) return;
                  setIsLoadingPRReport(true);
                  try {
                    const [owner, repo] = selectedRepo.split('/');
                    const newOffset = Math.max(0, prReportDaysOffset - 10);
                    const endDate = new Date();
                    endDate.setDate(endDate.getDate() - newOffset);
                    endDate.setHours(0, 0, 0, 0);
                    const endDateStr = endDate.toISOString().split('T')[0];
                    
                    const startDate = new Date(endDate);
                    startDate.setDate(startDate.getDate() - 10);
                    startDate.setHours(0, 0, 0, 0);
                    const startDateStr = startDate.toISOString().split('T')[0];
                    
                    const apiUrl = getApiBase();
                    const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/pr-report?startDate=${startDateStr}&endDate=${endDateStr}`);
                    
                    if (!response.ok) {
                      throw new Error(`Failed to fetch PR data: ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    const categorizedPRs = data.prs || { clean: [], problematic: [], blocking: [], minor: [], all: [] };
                    const categorization = data.categorization || { cleanCount: 0, issuesCount: 0, blockersCount: 0, minorCount: 0 };
                    
                    const reportWithCategorization = {
                      prs: categorizedPRs,
                      categorization: categorization,
                      statistics: data.statistics || {}
                    };
                    
                    setPrReport(reportWithCategorization);
                    setPrReportStats(data.statistics || {});
                    setPrReportDaysOffset(newOffset);
                    setPrFilter('all');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } catch (err) {
                    setError(`Error loading next period: ${err.message}`);
                  } finally {
                    setIsLoadingPRReport(false);
                  }
                }}
                disabled={prReportDaysOffset <= 0 || isLoadingPRReport}
                style={{
                  padding: '8px 12px',
                  backgroundColor: isLoadingPRReport ? '#FFB300' : (prReportDaysOffset <= 0 ? '#ccc' : '#667eea'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoadingPRReport || prReportDaysOffset <= 0 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  opacity: isLoadingPRReport ? 1 : (prReportDaysOffset <= 0 ? 0.6 : 1),
                  transition: 'all 0.2s'
                }}
              >
                {isLoadingPRReport ? '⏳ Loading...' : 'Next 10 Days →'}
              </button>
            </div>

            {/* Date Range Picker */}
            <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
              <button
                onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#138496'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#17a2b8'}
              >
                📅 {showDateRangePicker ? 'Hide' : 'Show'} Custom Date Range
              </button>

              {showDateRangePicker && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '6px', border: '1px solid #b3d9ff' }}>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={dateRangeStart}
                        onChange={(e) => {
                          const newStart = e.target.value;
                          setDateRangeStart(newStart);
                          // Auto-set end date if not set
                          if (!dateRangeEnd) {
                            const startDate = new Date(newStart);
                            const endDate = new Date(startDate);
                            endDate.setDate(endDate.getDate() + 30);
                            const endDateStr = endDate.toISOString().split('T')[0];
                            setDateRangeEnd(endDateStr);
                          }
                        }}
                        style={{
                          padding: '6px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          fontSize: '12px',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
                        End Date
                      </label>
                      <input
                        type="date"
                        value={dateRangeEnd}
                        onChange={(e) => setDateRangeEnd(e.target.value)}
                        style={{
                          padding: '6px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          fontSize: '12px',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    <div style={{ fontSize: '11px', color: '#666', minWidth: '140px' }}>
                      {dateRangeStart && dateRangeEnd ? (() => {
                        const start = new Date(dateRangeStart);
                        const end = new Date(dateRangeEnd);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        const status = diffDays > 30 ? '🔴 Too long' : `✓ ${diffDays} days`;
                        const statusColor = diffDays > 30 ? '#d32f2f' : '#388e3c';
                        return <span style={{ color: statusColor, fontWeight: 'bold' }}>{status}</span>;
                      })() : <span style={{ color: '#999' }}>Select dates</span>}
                    </div>

                    <button
                      onClick={async () => {
                        // Validate date range
                        if (!dateRangeStart || !dateRangeEnd) {
                          setError('Please select both start and end dates');
                          return;
                        }

                        const start = new Date(dateRangeStart);
                        const end = new Date(dateRangeEnd);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays > 30) {
                          setError('Date range cannot exceed 30 days');
                          return;
                        }

                        if (start > end) {
                          setError('Start date must be before end date');
                          return;
                        }

                        setIsLoadingPRReport(true);
                        try {
                          const [owner, repo] = selectedRepo.split('/');
                          const apiUrl = getApiBase();
                          const response = await authenticatedFetch(
                            `${apiUrl}/api/github/repos/${owner}/${repo}/pr-report?startDate=${dateRangeStart}&endDate=${dateRangeEnd}`
                          );

                          if (!response.ok) {
                            throw new Error(`Failed to fetch PR data: ${response.statusText}`);
                          }

                          const data = await response.json();
                          const categorizedPRs = data.prs || { clean: [], problematic: [], blocking: [], minor: [], all: [] };
                          const categorization = data.categorization || { cleanCount: 0, issuesCount: 0, blockersCount: 0, minorCount: 0 };

                          const reportWithCategorization = {
                            prs: categorizedPRs,
                            categorization: categorization,
                            statistics: data.statistics || {}
                          };

                          setPrReport(reportWithCategorization);
                          setPrReportStats(data.statistics || {});
                          setPrReportDaysOffset(0); // Reset offset for custom range
                          setPrFilter('all');
                          setShowDateRangePicker(false);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        } catch (err) {
                          setError(`Error loading custom date range: ${err.message}`);
                        } finally {
                          setIsLoadingPRReport(false);
                        }
                      }}
                      disabled={isLoadingPRReport || !dateRangeStart || !dateRangeEnd || (() => {
                        if (!dateRangeStart || !dateRangeEnd) return true;
                        const start = new Date(dateRangeStart);
                        const end = new Date(dateRangeEnd);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return diffDays > 30 || start > end;
                      })()}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: isLoadingPRReport ? '#FFB300' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        opacity: (isLoadingPRReport || !dateRangeStart || !dateRangeEnd || (() => {
                          if (!dateRangeStart || !dateRangeEnd) return true;
                          const start = new Date(dateRangeStart);
                          const end = new Date(dateRangeEnd);
                          const diffTime = Math.abs(end - start);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return diffDays > 30 || start > end;
                        })()) ? 0.5 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      {isLoadingPRReport ? '⏳ Loading...' : '🔍 Apply Range'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
            </>
          )}

          {/* Dashboard Header with Stats Cards */}
          <div style={{ marginBottom: '25px', padding: '20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '8px', color: 'white' }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px' }}>📊 PR Analysis Dashboard</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
              <div style={{ padding: '15px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px', borderLeft: '4px solid #388e3c' }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>🟢 Low Risk</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '5px' }}>{prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'low')?.length || 0}</div>
              </div>
              <div style={{ padding: '15px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px', borderLeft: '4px solid #fbc02d' }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>🟡 Medium Risk</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '5px' }}>{prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'medium')?.length || 0}</div>
              </div>
              <div style={{ padding: '15px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px', borderLeft: '4px solid #f57c00' }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>� High Risk</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '5px' }}>{prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'high')?.length || 0}</div>
              </div>
              <div style={{ padding: '15px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px', borderLeft: '4px solid #d32f2f' }}>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>� Critical Risk</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '5px' }}>{prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'critical')?.length || 0}</div>
              </div>
            </div>
          </div>

          {/* Filter Toggle Buttons */}
          <div style={{ marginBottom: '25px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setPrFilter('low')}
              style={{ 
                padding: '8px 16px', 
                cursor: 'pointer', 
                fontSize: '14px', 
                fontFamily: 'inherit',
                border: '2px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: prFilter === 'low' ? '#388e3c' : '#f8f9fa',
                color: prFilter === 'low' ? 'white' : '#000',
                fontWeight: prFilter === 'low' ? 'bold' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              � 🟢🟢 Low Risk ({prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'low')?.length || 0})
            </button>
            <button 
              onClick={() => setPrFilter('medium')}
              style={{ 
                padding: '8px 16px', 
                cursor: 'pointer', 
                fontSize: '14px', 
                fontFamily: 'inherit',
                border: '2px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: prFilter === 'medium' ? '#fbc02d' : '#f8f9fa',
                color: prFilter === 'medium' ? '#333' : '#000',
                fontWeight: prFilter === 'medium' ? 'bold' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              🟡 Medium Risk ({prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'medium')?.length || 0})
            </button>
            <button 
              onClick={() => setPrFilter('high')}
              style={{ 
                padding: '8px 16px', 
                cursor: 'pointer', 
                fontSize: '14px', 
                fontFamily: 'inherit',
                border: '2px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: prFilter === 'high' ? '#f57c00' : '#f8f9fa',
                color: prFilter === 'high' ? 'white' : '#000',
                fontWeight: prFilter === 'high' ? 'bold' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              🟠 High Risk ({prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'high')?.length || 0})
            </button>
            <button 
              onClick={() => setPrFilter('critical')}
              style={{ 
                padding: '8px 16px', 
                cursor: 'pointer', 
                fontSize: '14px', 
                fontFamily: 'inherit',
                border: '2px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: prFilter === 'critical' ? '#d32f2f' : '#f8f9fa',
                color: prFilter === 'critical' ? 'white' : '#000',
                fontWeight: prFilter === 'critical' ? 'bold' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              🔴 Critical Risk ({prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'critical')?.length || 0})
            </button>
            <button 
              onClick={() => setPrFilter('all')}
              style={{ 
                padding: '8px 16px', 
                cursor: 'pointer', 
                fontSize: '14px', 
                fontFamily: 'inherit',
                border: '2px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: prFilter === 'all' ? '#667eea' : '#f8f9fa',
                color: prFilter === 'all' ? 'white' : '#000',
                fontWeight: prFilter === 'all' ? 'bold' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              📊 All PRs ({prReport?.prs?.all?.length || 0})
            </button>
          </div>

          {/* Current Date Range Display */}
          {prReport && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '15px', 
              padding: '8px 12px', 
              backgroundColor: '#f0f4f8', 
              borderRadius: '4px',
              borderLeft: '3px solid #2196F3'
            }}>
              Showing {prReport?.prs?.all?.length || 0} PR{prReport?.prs?.all?.length !== 1 ? 's' : ''} in this period
            </div>
          )}

          {/* Filtered PR List */}
          <div style={{ marginBottom: '25px' }}>
            {(() => {
              const getFilteredPRs = () => {
                if (prFilter === 'low') return prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'low') || [];
                if (prFilter === 'medium') return prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'medium') || [];
                if (prFilter === 'high') return prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'high') || [];
                if (prFilter === 'critical') return prReport?.prs?.all?.filter(p => p.riskAnalysis?.riskLevel === 'critical') || [];
                return prReport?.prs?.all || [];
              };

              const filteredPRs = getFilteredPRs();

              return (
                <div>
                  <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
                    Showing {filteredPRs.length} PR{filteredPRs.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filteredPRs.map((pr, idx) => {
                      // Determine PR status purely from risk analysis
                      const hasRealIssues = pr.riskAnalysis?.detectedRisks?.length > 0 || pr.riskAnalysis?.codePatternWarnings?.length > 0;
                      const riskLevel = pr.riskAnalysis?.riskLevel || 'low';
                      const isClean = !hasRealIssues && (riskLevel === 'low');

                      // Border color based on risk level
                      const borderColor = isClean ? '#4ade80' : riskLevel === 'critical' ? '#dc3545' : riskLevel === 'high' ? '#f57c00' : riskLevel === 'medium' ? '#fbc02d' : '#4ade80';

                      return (
                        <div 
                          key={idx} 
                          style={{ 
                            padding: '12px', 
                            border: '1px solid #dee2e6', 
                            borderLeft: `4px solid ${borderColor}`,
                            borderRadius: '6px', 
                            backgroundColor: '#f8f9fa'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                                <a 
                                  href={pr.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  style={{ color: '#667eea', textDecoration: 'none' }}
                                >
                                  #{pr.number} {pr.title}
                                </a>
                              </div>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                By {pr.author} • {new Date(pr.closedAt).toLocaleDateString()}
                              </div>
                              <div style={{ fontSize: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
                                {isClean && <span style={{ color: '#4ade80', fontWeight: 'bold' }}>✅ Clean</span>}
                                {pr.riskAnalysis && (
                                  <button
                                    onClick={() => {
                                      console.log('[PullRequestForm] Clicking risk badge for PR #' + pr.number + ':', pr.riskAnalysis);
                                      setSelectedPRRisk(pr.riskAnalysis);
                                      setShowRiskModal(true);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <RiskBadge 
                                      riskLevel={pr.riskAnalysis.riskLevel} 
                                      riskScore={pr.riskAnalysis.riskScore}
                                    />
                                  </button>
                                )}
                              </div>
                              {/* Risk indicators preview — show top issues inline so users know what to look at */}
                              {hasRealIssues && (
                                <div style={{ fontSize: '11px', color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '4px 8px', marginBottom: '4px', lineHeight: '1.4' }}>
                                  {[...(pr.riskAnalysis?.detectedRisks || []), ...(pr.riskAnalysis?.codePatternWarnings || [])].slice(0, 2).map((risk, i) => {
                                    // Strip markdown links and emoji for a clean preview
                                    const riskStr = typeof risk === 'string' ? risk : (risk?.message || risk?.text || JSON.stringify(risk));
                                    const cleanRisk = riskStr.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/—\s*View.*$/i, '').trim();
                                    return <div key={i}>⚠️ {cleanRisk}</div>;
                                  })}
                                  {[...(pr.riskAnalysis?.detectedRisks || []), ...(pr.riskAnalysis?.codePatternWarnings || [])].length > 2 && (
                                    <div style={{ color: '#997a00', fontStyle: 'italic' }}>
                                      + {[...(pr.riskAnalysis?.detectedRisks || []), ...(pr.riskAnalysis?.codePatternWarnings || [])].length - 2} more — click risk badge for details
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Stats Summary */}
          {prReportStats && (
            <div>
              <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', flexWrap: 'wrap' }}>
                <div style={{ ...styles.scoreCard, color: '#000' }}><strong>Total:</strong> {prReportStats.totalClosedPRs}</div>
                <div style={{ ...styles.scoreCard, color: '#000' }}><strong>Approved:</strong> {prReportStats.approvedPRs}</div>
                <div style={{ ...styles.scoreCard, color: '#000' }}><strong>Changes:</strong> {prReportStats.needsChangesPRs}</div>
              </div>
              {prReportStats.pageStats && (
                <div style={{ fontSize: '12px', color: '#666', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', marginBottom: '20px', fontStyle: 'italic' }}>
                  💡 Note: Stats above show this page only ({prReportStats.pageSize} PRs). Older PRs can be loaded via the week filter below.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI REVIEW RESULTS */}
      {aiReview && (
        <div style={{ marginTop: '30px' }}>
          {/* Review Header */}
          <div style={{ ...styles.analysisResult, marginBottom: '0', borderBottomLeftRadius: '0', borderBottomRightRadius: '0', padding: '20px', backgroundColor: '#f4fff4' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#000' }}>🔍 PR Review Analysis</h3>
                <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>PR #{selectedPR} - {selectedPRTitle}</p>
              </div>
              <button 
                onClick={() => setAiReview(null)}
                style={{ padding: '6px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
              >
                ✕ Close
              </button>
            </div>

            {/* Verdict Badge */}
            <div style={{ marginBottom: '15px' }}>
              <span style={{
                display: 'inline-block',
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: 'bold',
                fontSize: '14px',
                backgroundColor: 
                  aiReview.review.verdict === 'APPROVED' ? '#d4edda' :
                  aiReview.review.verdict === 'CHANGES_REQUESTED' ? '#fff3cd' :
                  aiReview.review.verdict === 'BLOCKED' ? '#f8d7da' : '#e2e3e5',
                color:
                  aiReview.review.verdict === 'APPROVED' ? '#155724' :
                  aiReview.review.verdict === 'CHANGES_REQUESTED' ? '#856404' :
                  aiReview.review.verdict === 'BLOCKED' ? '#721c24' : '#383d41'
              }}>
                {aiReview.review.verdict === 'APPROVED' && '✅ APPROVED'}
                {aiReview.review.verdict === 'CHANGES_REQUESTED' && '⚠️ CHANGES REQUESTED'}
                {aiReview.review.verdict === 'BLOCKED' && '🚫 BLOCKED'}
                {!['APPROVED', 'CHANGES_REQUESTED', 'BLOCKED'].includes(aiReview.review.verdict) && `📋 ${aiReview.review.verdict}`}
              </span>
            </div>

            {/* Summary */}
            <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50', borderRadius: '4px', marginBottom: '15px' }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#2e7d32', lineHeight: '1.5' }}>
                {aiReview.review.summary}
              </p>
            </div>

            {/* Scores */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              <div style={{ padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '6px', borderLeft: '4px solid #2196f3', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#1976d2', fontWeight: 'bold' }}>Rules Compliance</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2', marginTop: '4px' }}>{aiReview.scores.rulesCompliance}%</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: '#ffe8e8', borderRadius: '6px', borderLeft: '4px solid #f44336', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#c62828', fontWeight: 'bold' }}>Risk Level</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f44336', marginTop: '4px' }}>{aiReview.scores.riskLevel}%</div>
              </div>
              {aiReview.scores.overallScore !== undefined && (
                <div style={{ padding: '12px', backgroundColor: '#f3e5f5', borderRadius: '6px', borderLeft: '4px solid #9c27b0', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6a1b9a', fontWeight: 'bold' }}>Overall Score</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#9c27b0', marginTop: '4px' }}>{aiReview.scores.overallScore}%</div>
                </div>
              )}
            </div>
          </div>

          {/* PR Changes Summary */}
          {(aiReview.review.filesChanged || aiReview.review.additionsCount || aiReview.review.deletionsCount) && (
            <div style={{ padding: '15px', backgroundColor: '#f5f5f5', borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd', display: 'flex', gap: '20px', justifyContent: 'center', fontSize: '13px', color: '#666' }}>
              {aiReview.review.filesChanged && <div>📄 <strong>{aiReview.review.filesChanged}</strong> file{aiReview.review.filesChanged !== 1 ? 's' : ''} changed</div>}
              {aiReview.review.additionsCount && <div>➕ <strong>{aiReview.review.additionsCount}</strong> additions</div>}
              {aiReview.review.deletionsCount && <div>➖ <strong>{aiReview.review.deletionsCount}</strong> deletions</div>}
            </div>
          )}

          {/* Rule Violations */}
          {aiReview.review.violations && aiReview.review.violations.length > 0 && (
            <div style={{ padding: '20px', backgroundColor: '#fff', borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#d32f2f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🚫 Rule Violations ({aiReview.review.violations.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {aiReview.review.violations.map((violation, idx) => {
                  // Build GitHub link if file and line info available
                  let githubLink = null;
                  if (violation.file && violation.line && selectedPR) {
                    // Link to the PR's files changed tab, anchored to the specific file
                    githubLink = `${aiReview?.prUrl || ''}#diff-${violation.file.replace(/\//g, '-').replace(/\./g, '-')}R${violation.line}`;
                  }
                  
                  return (
                    <div key={idx} style={{ padding: '12px', backgroundColor: '#ffebee', border: '1px solid #ef5350', borderRadius: '6px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#c62828', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>⚠️ {violation.rule || 'Violation'}</span>
                        {githubLink && (
                          <a 
                            href={githubLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: '11px', 
                              color: '#c62828', 
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              marginLeft: '8px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            View on GitHub →
                          </a>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                        {violation.message || violation.description}
                      </div>
                      {violation.file && violation.line && (
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>
                          📁 {violation.file}:{violation.line}
                        </div>
                      )}
                      {violation.severity && (
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          Severity: <strong>{violation.severity}</strong>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Issues */}
          {aiReview.review.issues && aiReview.review.issues.length > 0 && (
            <div style={{ padding: '20px', backgroundColor: '#fff', borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#f57c00', display: 'flex', alignItems: 'center', gap: '8px' }}>
                ⚠️ Issues Found ({aiReview.review.issues.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {aiReview.review.issues.map((issue, idx) => {
                  // Build GitHub link if file and line info available
                  let githubLink = null;
                  if (issue.file && issue.line && selectedPR) {
                    githubLink = `${aiReview?.prUrl || ''}#diff-${issue.file.replace(/\//g, '-').replace(/\./g, '-')}R${issue.line}`;
                  }
                  
                  return (
                    <div key={idx} style={{ padding: '12px', backgroundColor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: '6px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#e65100', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{issue.title || issue.issue}</span>
                        {githubLink && (
                          <a 
                            href={githubLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: '11px', 
                              color: '#e65100', 
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              marginLeft: '8px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            View on GitHub →
                          </a>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                        {issue.description || issue.message}
                      </div>
                      {issue.file && (
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                          📁 {issue.file}
                          {issue.line && <span>:{issue.line}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {aiReview.review.recommendations && aiReview.review.recommendations.length > 0 && (
            <div style={{ padding: '20px', backgroundColor: '#fff', borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#1976d2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                💡 Recommendations ({aiReview.review.recommendations.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {aiReview.review.recommendations.map((rec, idx) => (
                  <div key={idx} style={{ padding: '12px', backgroundColor: '#e3f2fd', border: '1px solid #64b5f6', borderRadius: '6px' }}>
                    <div style={{ fontSize: '13px', color: '#1565c0', lineHeight: '1.5' }}>
                      {rec.recommendation || rec.text || rec}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Changed Files with Detailed Review */}
          {aiReview.review.changedFiles && aiReview.review.changedFiles.length > 0 && (
            <div style={{ padding: '20px', backgroundColor: '#fff', borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#424242', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📄 Changed Files ({aiReview.review.changedFiles.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {aiReview.review.changedFiles.map((file, idx) => {
                  const fileData = typeof file === 'string' ? { name: file } : file;
                  const fileName = fileData.name || file;
                  const fileExtension = fileName.split('.').pop()?.toLowerCase() || 'unknown';
                  const fileIssues = aiReview.review.issues?.filter(issue => issue.file === fileName) || [];
                  const fileViolations = aiReview.review.violations?.filter(v => v.file === fileName) || [];
                  const fileRecommendations = fileData.recommendations || [];
                  
                  // Determine file type icon and severity color
                  let fileIcon = '📄';
                  let severityColor = '#4caf50'; // Green - no issues
                  
                  if (fileExtension === 'js' || fileExtension === 'jsx') fileIcon = '⚙️';
                  else if (fileExtension === 'ts' || fileExtension === 'tsx') fileIcon = '🔷';
                  else if (fileExtension === 'json') fileIcon = '📋';
                  else if (fileExtension === 'css' || fileExtension === 'scss') fileIcon = '🎨';
                  else if (fileExtension === 'md') fileIcon = '📝';
                  else if (fileExtension === 'html') fileIcon = '🌐';
                  
                  if (fileViolations.length > 0) severityColor = '#f44336'; // Red - violations
                  else if (fileIssues.length > 0) severityColor = '#ff9800'; // Orange - issues
                  
                  return (
                    <details key={idx} style={{ cursor: 'pointer', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: `2px solid ${severityColor}`, transition: 'all 0.2s' }}>
                      <summary style={{ display: 'flex', alignItems: 'center', gap: '12px', fontWeight: 'bold', fontSize: '14px', color: '#333', userSelect: 'none', cursor: 'pointer', listStyle: 'none' }}>
                        <span style={{ fontSize: '16px', marginRight: '4px', transform: 'rotate(0deg)', transition: 'transform 0.2s' }}>▶</span>
                        <span style={{ fontSize: '18px' }}>{fileIcon}</span>
                        <span style={{ flex: 1, fontFamily: 'monospace', color: '#666' }}>{fileName}</span>
                        <span style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#999' }}>
                          {fileViolations.length > 0 && <span style={{ backgroundColor: '#f44336', color: 'white', padding: '2px 8px', borderRadius: '3px', fontWeight: 'bold' }}>{fileViolations.length} violation{fileViolations.length !== 1 ? 's' : ''}</span>}
                          {fileIssues.length > 0 && <span style={{ backgroundColor: '#ff9800', color: 'white', padding: '2px 8px', borderRadius: '3px', fontWeight: 'bold' }}>{fileIssues.length} issue{fileIssues.length !== 1 ? 's' : ''}</span>}
                          {fileViolations.length === 0 && fileIssues.length === 0 && <span style={{ backgroundColor: '#4caf50', color: 'white', padding: '2px 8px', borderRadius: '3px', fontWeight: 'bold' }}>✓ Clean</span>}
                        </span>
                      </summary>
                      
                      {/* File Details */}
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>
                        {/* File Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px', fontSize: '12px' }}>
                          {fileData.additions && (
                            <div style={{ padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px', textAlign: 'center' }}>
                              <div style={{ color: '#2e7d32', fontWeight: 'bold' }}>+{fileData.additions}</div>
                              <div style={{ color: '#666', fontSize: '11px' }}>additions</div>
                            </div>
                          )}
                          {fileData.deletions && (
                            <div style={{ padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px', textAlign: 'center' }}>
                              <div style={{ color: '#c62828', fontWeight: 'bold' }}>-{fileData.deletions}</div>
                              <div style={{ color: '#666', fontSize: '11px' }}>deletions</div>
                            </div>
                          )}
                          <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', textAlign: 'center' }}>
                            <div style={{ color: '#333', fontWeight: 'bold' }}>{fileExtension}</div>
                            <div style={{ color: '#666', fontSize: '11px' }}>file type</div>
                          </div>
                        </div>

                        {/* File Summary/Description */}
                        {fileData.summary && (
                          <div style={{ padding: '10px', backgroundColor: '#e3f2fd', borderLeft: '3px solid #2196f3', borderRadius: '4px', marginBottom: '12px', fontSize: '13px', color: '#1565c0', lineHeight: '1.5' }}>
                            📝 {fileData.summary}
                          </div>
                        )}

                        {/* File Violations */}
                        {fileViolations.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#f44336', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              🚫 Violations ({fileViolations.length})
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {fileViolations.map((violation, vidx) => (
                                <div key={vidx} style={{ padding: '10px', backgroundColor: '#ffebee', borderLeft: '3px solid #f44336', borderRadius: '4px', fontSize: '12px' }}>
                                  <div style={{ fontWeight: 'bold', color: '#c62828', marginBottom: '2px' }}>
                                    {violation.rule || violation.title}
                                  </div>
                                  <div style={{ color: '#666', marginBottom: '2px' }}>
                                    {violation.message || violation.description}
                                  </div>
                                  {violation.severity && (
                                    <div style={{ fontSize: '11px', color: '#999' }}>
                                      Severity: <strong>{violation.severity}</strong>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* File Issues */}
                        {fileIssues.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#ff9800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              ⚠️ Issues ({fileIssues.length})
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {fileIssues.map((issue, iidx) => {
                                // Find related violations for this issue
                                const relatedViolations = fileViolations.filter(v => {
                                  const issueTitle = (issue.title || issue.issue || '').toLowerCase();
                                  const violationRule = (v.rule || v.title || '').toLowerCase();
                                  return violationRule.includes(issueTitle.split(' ')[0]) || issueTitle.includes(violationRule.split(' ')[0]);
                                });
                                
                                return (
                                  <details key={iidx} style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#fff3e0', borderLeft: '3px solid #ff9800', borderRadius: '4px', fontSize: '12px', transition: 'all 0.2s' }}>
                                    <summary style={{ fontWeight: 'bold', color: '#e65100', marginBottom: '0px', cursor: 'pointer', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '12px', marginRight: '4px' }}>▶</span>
                                      {issue.title || issue.issue}
                                    </summary>
                                    
                                    {/* Issue Details Dropdown */}
                                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ffe0b2' }}>
                                      <div style={{ color: '#666', marginBottom: '8px', lineHeight: '1.4' }}>
                                        {issue.description || issue.message}
                                      </div>
                                      
                                      {issue.line && (
                                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '8px' }}>
                                          <strong>📍 Location:</strong> Line {issue.line}
                                        </div>
                                      )}
                                      
                                      {/* Related Rules/Violations */}
                                      {relatedViolations.length > 0 && (
                                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#ffebee', borderLeft: '3px solid #f44336', borderRadius: '4px' }}>
                                          <div style={{ fontWeight: 'bold', color: '#c62828', marginBottom: '6px', fontSize: '11px' }}>
                                            🚫 Rules Broken:
                                          </div>
                                          {relatedViolations.map((v, vidx) => (
                                            <div key={vidx} style={{ fontSize: '11px', color: '#666', marginBottom: '4px', paddingLeft: '8px', borderLeft: '2px solid #ef5350' }}>
                                              <strong>{v.rule || v.title}</strong>
                                              {v.message && <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>{v.message}</div>}
                                              {v.severity && <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>Severity: {v.severity}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </details>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* File Recommendations */}
                        {fileRecommendations.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <h5 style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#1976d2', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              💡 Recommendations
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {fileRecommendations.map((rec, ridx) => (
                                <div key={ridx} style={{ padding: '10px', backgroundColor: '#e3f2fd', borderLeft: '3px solid #2196f3', borderRadius: '4px', fontSize: '12px', color: '#1565c0', lineHeight: '1.4' }}>
                                  {rec.recommendation || rec.text || rec}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Clean File Message */}
                        {fileViolations.length === 0 && fileIssues.length === 0 && fileRecommendations.length === 0 && (
                          <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderLeft: '3px solid #4caf50', borderRadius: '4px', fontSize: '12px', color: '#2e7d32', textAlign: 'center', fontWeight: 'bold' }}>
                            ✅ No issues found - this file looks good!
                          </div>
                        )}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rules Used Section */}
          <div style={{ padding: '20px', backgroundColor: '#fff', borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}>
            <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#424242', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📋 Rules Applied
            </h4>
            <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #e0e0e0', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>File: {localRulesFile}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Size: {guidelineRules.length} bytes</div>
            </div>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fafafa', 
              borderRadius: '6px', 
              border: '1px solid #e0e0e0',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#333',
              maxHeight: '300px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: '1.4'
            }}>
              {guidelineRules}
            </div>
          </div>

          {/* Rules Used Footer with Email Button */}
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderTop: '1px solid #ddd', fontSize: '12px', color: '#999', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              ✅ Analysis completed using rules from: <strong>{localRulesFile}</strong>
            </div>
            <button
              onClick={handleOpenEmailClient}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
            >
              📧 Send Report via Email
            </button>
          </div>
        </div>
      )}

      {/* Risk Detail Modal */}
      <RiskDetailModal
        isOpen={showRiskModal}
        prRisk={selectedPRRisk}
        onClose={() => setShowRiskModal(false)}
        authService={authService}
      />
        </>
      )}

      {/* Bug Hunt Tab Content */}
      {codeAiSubTab === 'bug-hunt' && (
        <CodeAITab selectedProject={selectedProject} isActive={isActive} selectedModel={selectedModel} selectedRepo={selectedRepo} repoLoading={loading} />
      )}

      {/* Branch Review Tab Content */}
      {codeAiSubTab === 'branch-review' && (
        <BranchReviewPanel
          selectedRepo={selectedRepo}
          selectedModel={selectedModel}
          authenticatedFetch={authenticatedFetch}
          branches={branches}
          setBranches={setBranches}
          selectedBranch={selectedBranch}
          setSelectedBranch={setSelectedBranch}
          branchSearch={branchSearch}
          setBranchSearch={setBranchSearch}
          loadingBranches={loadingBranches}
          setLoadingBranches={setLoadingBranches}
          branchAnalysis={branchAnalysis}
          setBranchAnalysis={setBranchAnalysis}
          isAnalyzingBranch={isAnalyzingBranch}
          setIsAnalyzingBranch={setIsAnalyzingBranch}
          branchError={branchError}
          setBranchError={setBranchError}
        />
      )}

      {/* File Analysis Tab Content */}
      {codeAiSubTab === 'file-analysis' && (
        <FileAnalysisPanel
          selectedRepo={selectedRepo}
          selectedModel={selectedModel}
          authenticatedFetch={authenticatedFetch}
          fileTree={fileTree}
          setFileTree={setFileTree}
          fileTreePath={fileTreePath}
          setFileTreePath={setFileTreePath}
          fileTreeRef={fileTreeRef}
          setFileTreeRef={setFileTreeRef}
          selectedAnalysisFile={selectedAnalysisFile}
          setSelectedAnalysisFile={setSelectedAnalysisFile}
          fileAnalysis={fileAnalysis}
          setFileAnalysis={setFileAnalysis}
          isAnalyzingFile={isAnalyzingFile}
          setIsAnalyzingFile={setIsAnalyzingFile}
          fileAnalysisError={fileAnalysisError}
          setFileAnalysisError={setFileAnalysisError}
          loadingFileTree={loadingFileTree}
          setLoadingFileTree={setLoadingFileTree}
          fileTreeBreadcrumb={fileTreeBreadcrumb}
          setFileTreeBreadcrumb={setFileTreeBreadcrumb}
          branches={branches}
          setBranches={setBranches}
        />
      )}
    </div>
  );
};

const styles = {
  form: { padding: '2rem', maxWidth: '1000px', margin: '0 auto', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif", fontSize: '14px' },
  section: { marginBottom: '2rem' },
  label: { display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' },
  searchInput: { width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '14px' },
  select: { width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', backgroundColor: '#fff', color: '#000' },
  button: { padding: '12px 20px', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', fontFamily: 'inherit' },
  error: { padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '15px', fontSize: '14px' },
  scoreCard: { padding: '10px 20px', border: '1px solid #eee', borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '14px' },
  analysisResult: { padding: '20px', border: '1px solid #28a745', borderRadius: '8px', backgroundColor: '#f4fff4', fontSize: '14px' }
};

// Risk Detail Modal Component
function RiskDetailModal({ isOpen, prRisk, onClose, authService }) {
  const [showRawData, setShowRawData] = useState(false);
  const [codeSnippets, setCodeSnippets] = useState({}); // Cache code snippets
  const [loadingSnippet, setLoadingSnippet] = useState(null); // Track which snippet is loading
  const [aiValidation, setAiValidation] = useState({}); // Cache AI validation results
  const [askingAI, setAskingAI] = useState(null); // Track which warning is being validated
  const [showRiskExplanation, setShowRiskExplanation] = useState(false); // Show risk explanation modal
  const [selectedRiskForExplanation, setSelectedRiskForExplanation] = useState(null); // Selected risk to explain
  const [riskExplanation, setRiskExplanation] = useState(null); // AI explanation for risk
  const [explainRiskLoading, setExplainRiskLoading] = useState(false); // Loading state for explanation

  // Get token from authService on mount (BEFORE early return)
  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (authService) {
          const accessToken = await authService.getAccessToken();
          console.log('[RiskDetailModal] Token loaded from authService:', !!accessToken);
        }
      } catch (err) {
        console.error('[RiskDetailModal] Failed to get token:', err);
      }
    };
    
    if (isOpen) {
      fetchToken();
    }
  }, [isOpen, authService]);

  if (!isOpen || !prRisk) return null;

  // Log raw data from server BEFORE processing
  console.log('[RiskDetailModal] RAW prRisk data from server:', JSON.stringify(prRisk, null, 2));
  console.log('[RiskDetailModal] prRisk.prNumber:', prRisk.prNumber);
  console.log('[RiskDetailModal] prRisk.prUrl:', prRisk.prUrl);
  console.log('[RiskDetailModal] prRisk.prTitle:', prRisk.prTitle);
  console.log('[RiskDetailModal] detectedRisks:', prRisk.detectedRisks);
  console.log('[RiskDetailModal] codePatternWarnings:', prRisk.codePatternWarnings);

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical':
        return '#d32f2f';
      case 'high':
        return '#f57c00';
      case 'medium':
        return '#fbc02d';
      case 'low':
        return '#388e3c';
      default:
        return '#666';
    }
  };

  // Fetch code snippet from GitHub PR diff
  const fetchCodeSnippet = async (filePath, lineNumber, warningIdx) => {
    const cacheKey = `${filePath}:${lineNumber}`;
    // Allow reload - removed early return that prevented re-fetching

    setLoadingSnippet(cacheKey);
    try {
      const apiUrl = getApiBase();
      
      // Extract owner/repo/prNumber from prRisk.prUrl
      const urlMatch = prRisk.prUrl?.match(/github\.com\/([^/]+)\/([^/]+)\/pull\/(\d+)/);
      const owner = urlMatch?.[1];
      const repo = urlMatch?.[2];
      const prNumberFromUrl = urlMatch?.[3];
      
      if (!owner || !repo || !prNumberFromUrl) {
        console.error('Could not extract owner/repo/prNumber from PR URL:', prRisk.prUrl);
        setCodeSnippets(prev => ({
          ...prev,
          [cacheKey]: { error: 'Could not parse PR URL' }
        }));
        return;
      }

      console.log('[API-SNIPPET] ========== REQUEST PARAMETERS ==========');
      console.log('[API-SNIPPET] Owner:', owner);
      console.log('[API-SNIPPET] Repo:', repo);
      console.log('[API-SNIPPET] PR Number from URL:', prNumberFromUrl);
      console.log('[API-SNIPPET] PR Number from prRisk:', prRisk.prNumber);
      if (prNumberFromUrl !== prRisk.prNumber?.toString()) {
        console.warn('[API-SNIPPET] ⚠️ MISMATCH: URL PR# differs from prRisk.prNumber! Using URL value:', prNumberFromUrl);
      }
      console.log('[API-SNIPPET] File Path:', filePath);
      console.log('[API-SNIPPET] Line Number:', lineNumber);
      console.log('[API-SNIPPET] prRisk.prUrl:', prRisk.prUrl);
      console.log('[API-SNIPPET] ===================================');
      
      const fetchUrl = `${apiUrl}/api/github/repos/${owner}/${repo}/pulls/${prNumberFromUrl}/file-snippet?filePath=${encodeURIComponent(filePath)}&lineNumber=${lineNumber}`;
      const fetchHeaders = {
        'Content-Type': 'application/json'
      };
      
      console.log('[API-SNIPPET] Full URL:', fetchUrl);
      console.log('[API-SNIPPET] Sending fetch without auth (backend uses GITHUB_TOKEN)');
      
      const response = await fetch(fetchUrl, {
        headers: fetchHeaders
      });

      console.log(`[API-SNIPPET] Response status: ${response.status} ${response.statusText}`);
      console.log(`[API-SNIPPET] Response headers:`, {
        'content-type': response.headers.get('content-type'),
        'content-length': response.headers.get('content-length')
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          console.log('[API-SNIPPET] Error response body:', errorBody);
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
          }
        } catch (e) {
          console.log('[API-SNIPPET] Could not read error body');
        }
        throw new Error(`Failed to fetch snippet: ${errorMessage}`);
      }

      const data = await response.json();
      console.log(`[API-SNIPPET] Success! Got code snippet with ${data.beforeCode?.length || 0} before, ${data.afterCode?.length || 0} after`);
      
      setCodeSnippets(prev => ({
        ...prev,
        [cacheKey]: {
          filePath,
          lineNumber,
          code: data.code || '',
          beforeCode: data.beforeCode || [],
          afterCode: data.afterCode || [],
          language: data.language || 'text'
        }
      }));
    } catch (err) {
      console.error('[API-SNIPPET] FINAL ERROR:', err.message);
      console.error('[API-SNIPPET] Error stack:', err.stack);
      setCodeSnippets(prev => ({
        ...prev,
        [cacheKey]: {
          filePath,
          lineNumber,
          error: err.message,
          code: 'Failed to load code snippet'
        }
      }));
    } finally {
      setLoadingSnippet(null);
    }
  };

  // Ask AI to validate if the warning is a real issue
  const askAIValidation = async (warning, warningIdx, codeSnippet) => {
    const cacheKey = `${warning.files?.[0]}:${warning.lineNumbers?.[0]}`;
    // Allow re-running - don't return early if already validated

    setAskingAI(cacheKey);
    try {
      const apiUrl = getApiBase();
      
      // ALWAYS get fresh token from authService - don't rely on state
      let authToken = null;
      try {
        if (authService) {
          authToken = await authService.getAccessToken();
          console.log('[AI-VALIDATE] Got fresh token from authService:', !!authToken);
        }
      } catch (err) {
        console.warn('[AI-VALIDATE] Could not get token from authService:', err.message);
        // Fallback to localStorage
        authToken = localStorage.getItem('authToken') || 
                    sessionStorage.getItem('authToken') ||
                    localStorage.getItem('token') ||
                    sessionStorage.getItem('token');
      }
      
      console.log('[AI-VALIDATE] DEBUG: Using token...');
      console.log('[AI-VALIDATE] Token found:', !!authToken);
      console.log('[AI-VALIDATE] Token length:', authToken?.length || 0);
      console.log('[AI-VALIDATE] Token preview:', authToken ? authToken.substring(0, 50) + '...' : 'NO TOKEN');
      console.log('[AI-VALIDATE] API URL:', apiUrl);
      
      const prompt = `
I found a potential security issue in the code:

**Warning:** ${warning.title}
**Description:** ${warning.description}
**File:** ${warning.files?.[0]}
**Line:** ${warning.lineNumbers?.[0]}
**Severity:** ${warning.severity}

**Code Context:**
\`\`\`${codeSnippet.language || 'text'}
${codeSnippet.beforeCode?.join('\n') || ''}
>>> ${codeSnippet.code} <<<
${codeSnippet.afterCode?.join('\n') || ''}
\`\`\`

Please analyze this code and determine:
1. Is this actually a security vulnerability or false positive?
2. If it is a real issue, what's the severity and why?
3. If it's a false positive, explain why this code is safe
4. What would be the recommended fix?

Provide a concise, technical analysis.
`;

      const response = await fetch(`${apiUrl}/api/ai/validate-security-issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          prompt,
          warning,
          codeSnippet
        })
      });

      console.log('[AI-VALIDATE] Headers being sent:', {
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken.substring(0, 50)}...` : 'NOT INCLUDED'
      });
      console.log('[AI-VALIDATE] Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorDetail = '';
        try {
          const errorBody = await response.text();
          console.log('[AI-VALIDATE] Error response body:', errorBody);
          errorDetail = errorBody ? ` - ${errorBody}` : '';
        } catch (e) {
          console.log('[AI-VALIDATE] Could not read error body');
        }
        throw new Error(`Failed to get AI validation: ${response.statusText}${errorDetail}`);
      }

      const data = await response.json();
      console.log('[AI-VALIDATE] Full response data:', JSON.stringify(data, null, 2));
      console.log('[AI-VALIDATE] isRealIssue:', data.isRealIssue);
      console.log('[AI-VALIDATE] analysis:', data.analysis);
      console.log('[AI-VALIDATE] recommendation:', data.recommendation);
      
      const validationData = {
        isRealIssue: data.isRealIssue,
        severity: data.severity,
        analysis: data.analysis,
        recommendation: data.recommendation
      };
      
      console.log('[AI-VALIDATE] Storing validation:', JSON.stringify(validationData, null, 2));
      setAiValidation(prev => {
        const updated = {
          ...prev,
          [cacheKey]: validationData
        };
        console.log('[AI-VALIDATE] Updated validation state:', JSON.stringify(updated, null, 2));
        return updated;
      });
    } catch (err) {
      console.error('[AI-VALIDATE] Error getting AI validation:', err);
      console.error('[AI-VALIDATE] Error details:', err.message);
      setAiValidation(prev => ({
        ...prev,
        [cacheKey]: {
          error: err.message,
          analysis: 'Failed to get AI analysis. Please try again.'
        }
      }));
    } finally {
      setAskingAI(null);
    }
  };

  // Ask AI to explain a detected risk in more detail
  const askAIExplainRisk = async (riskText) => {
    setExplainRiskLoading(true);
    try {
      const apiUrl = getApiBase();
      
      // ALWAYS get fresh token from authService
      let authToken = null;
      if (authService) {
        authToken = await authService.getAccessToken();
      }

      const response = await fetch(`${apiUrl}/api/ai/explain-detected-risk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify({
          riskText: riskText,
          prTitle: prRisk?.prTitle || '',
          prUrl: prRisk?.prUrl || ''
        })
      });

      if (!response.ok) {
        throw new Error(`AI API returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setRiskExplanation(data.explanation || 'No explanation available');
    } catch (err) {
      console.error('Failed to get risk explanation:', err);
      setRiskExplanation(`Failed to get explanation: ${err.message}`);
    } finally {
      setExplainRiskLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '30px',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>Risk Analysis: PR #{prRisk.prNumber}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}><strong>Title:</strong> {prRisk.prTitle}</p>
          <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
            <strong>Risk Score:</strong>{' '}
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '12px',
                backgroundColor: getRiskColor(prRisk.riskLevel),
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              {prRisk.riskScore}/100 ({prRisk.riskLevel.toUpperCase()})
            </span>
          </p>
        </div>

        {/* RAW DATA SECTION - Display exactly what server sends */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', border: '2px solid #667eea', borderRadius: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: '0', color: '#667eea', fontSize: '14px', fontWeight: 'bold' }}>📊 RAW DATA FROM SERVER:</h4>
            <button
              onClick={() => setShowRawData(!showRawData)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: showRawData ? '#667eea' : '#ddd',
                color: showRawData ? '#fff' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              {showRawData ? '▼ Hide' : '▶ Show'}
            </button>
          </div>
          {showRawData && (
            <div style={{ 
              backgroundColor: '#fff', 
              padding: '12px', 
              borderRadius: '4px', 
              fontFamily: 'monospace', 
              fontSize: '11px',
              maxHeight: '400px',
              overflowY: 'auto',
              color: '#333',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: '1.4',
              marginTop: '10px'
            }}>
              <div><strong>detectedRisks:</strong></div>
              <div style={{ color: '#0066cc', marginBottom: '12px' }}>
                {JSON.stringify(prRisk.detectedRisks, null, 2)}
              </div>
              <div><strong>codePatternWarnings:</strong></div>
              <div style={{ color: '#cc0000' }}>
                {JSON.stringify(prRisk.codePatternWarnings, null, 2)}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>Detected Risks:</h4>
          {prRisk.detectedRisks && Array.isArray(prRisk.detectedRisks) && prRisk.detectedRisks.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {prRisk.detectedRisks.map((risk, idx) => (
                <div key={idx}>
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fff3cd',
                      borderLeft: '3px solid #ffc107',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: '#555', fontSize: '14px', fontWeight: '500', flex: 1 }}>
                      {(() => {
                        // Parse markdown links [text](url) into clickable <a> tags
                        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
                        const parts = [];
                        let lastIndex = 0;
                        let match;
                        const riskText = risk;
                        while ((match = linkRegex.exec(riskText)) !== null) {
                          if (match.index > lastIndex) {
                            parts.push(riskText.substring(lastIndex, match.index));
                          }
                          parts.push(
                            <a
                              key={match.index}
                              href={match[2]}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#0366d6', textDecoration: 'underline', fontWeight: '600' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {match[1]}
                            </a>
                          );
                          lastIndex = match.index + match[0].length;
                        }
                        if (lastIndex < riskText.length) {
                          parts.push(riskText.substring(lastIndex));
                        }
                        return parts.length > 0 ? parts : riskText;
                      })()}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedRiskForExplanation(risk);
                        setShowRiskExplanation(true);
                        askAIExplainRisk(risk);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: '2px 6px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#ffc107',
                        color: '#333',
                        fontWeight: 'bold',
                        transition: 'all 0.2s',
                        marginLeft: '10px',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#ffb300';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#ffc107';
                      }}
                      title="Get AI explanation for this risk"
                    >
                      ?
                    </button>
                  </div>
                  <RiskDetailsViewer risk={risk} prData={prRisk} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '16px', backgroundColor: '#e8f5e9', borderLeft: '3px solid #4caf50', borderRadius: '4px' }}>
              <p style={{ color: '#2e7d32', fontSize: '14px', margin: '0', fontWeight: '500' }}>
                ✅ No risks detected — this PR looks clean!
              </p>
              <p style={{ color: '#558b2f', fontSize: '12px', margin: '8px 0 0 0' }}>
                PR was reviewed, comments resolved, and merged following best practices.
              </p>
            </div>
          )}
        </div>

        {prRisk.codePatternWarnings && Array.isArray(prRisk.codePatternWarnings) && prRisk.codePatternWarnings.length > 0 && (
          <div style={{ marginBottom: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#d32f2f', fontSize: '16px' }}>⚠️ Code Pattern Warnings:</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {prRisk.codePatternWarnings.map((warning, idx) => {
                console.log(`[RiskModal] Warning ${idx}:`, warning);
                const isString = typeof warning === 'string';
                const filePath = !isString && warning.files?.[0];
                const lineNumber = !isString && warning.lineNumbers?.[0];
                const codeSnippetsData = !isString && warning.codeSnippets ? warning.codeSnippets : [];
                console.log(`[RiskModal] Warning ${idx} - filePath: ${filePath}, lineNumber: ${lineNumber}, codeSnippets: ${codeSnippetsData.length}`);
                const cacheKey = `${filePath}:${lineNumber}`;
                const snippet = codeSnippets[cacheKey];
                const validation = aiValidation[cacheKey];
                const severity = !isString && warning.severity ? warning.severity : 'medium';
                
                const severityColor = {
                  critical: '#d32f2f',
                  high: '#f57c00',
                  medium: '#fbc02d',
                  low: '#388e3c'
                }[severity] || '#d32f2f';

                return (
                  <details key={idx} style={{
                    padding: '12px',
                    backgroundColor: severity === 'critical' ? '#ffebee' : (severity === 'high' ? '#fff3e0' : '#fffde7'),
                    borderLeft: `4px solid ${severityColor}`,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}>
                    <summary style={{
                      fontWeight: 'bold',
                      color: severityColor,
                      cursor: 'pointer',
                      userSelect: 'none',
                      listStyle: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '12px', marginRight: '4px' }}>▶</span>
                      {!isString && warning.icon ? `${warning.icon} ` : ''}
                      {!isString ? warning.title : warning}
                    </summary>

                    {/* Expanded Details */}
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${severityColor}` }}>
                      {/* Description */}
                      {!isString && warning.description && (
                        <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px', fontStyle: 'italic' }}>
                          {warning.description}
                        </p>
                      )}

                      {/* File Info */}
                      {filePath && (
                        <div style={{ fontSize: '12px', marginBottom: '12px', padding: '8px', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '4px', fontFamily: 'monospace', color: '#000000', wordWrap: 'break-word', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          <div style={{ marginBottom: '6px' }}>
                            <strong style={{ color: '#000000' }}>📁 File:</strong> <span style={{ color: '#000000', wordBreak: 'break-all' }}>{filePath}</span>
                          </div>
                          {lineNumber && <div><strong style={{ color: '#000000' }}>📍 Line:</strong> <span style={{ color: '#000000' }}>{lineNumber}</span></div>}
                        </div>
                      )}

                      {/* Inline Code Snippets from Warning Data */}
                      {!isString && warning.codeSnippets && Array.isArray(warning.codeSnippets) && warning.codeSnippets.length > 0 && (
                        <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', borderLeft: '3px solid #d32f2f', fontFamily: 'monospace', fontSize: '11px', maxHeight: '150px', overflowY: 'auto' }}>
                          <div style={{ fontWeight: 'bold', color: '#d32f2f', marginBottom: '6px' }}>Detected Code:</div>
                          {warning.codeSnippets.slice(0, 3).map((snippet, i) => (
                            <div key={i} style={{ marginBottom: '6px', borderBottom: '1px solid #ddd', paddingBottom: '6px' }}>
                              <span style={{ color: '#666' }}>Line {snippet.line}:</span>
                              <span style={{ color: '#000', wordBreak: 'break-all', marginLeft: '6px' }}>{snippet.code}</span>
                            </div>
                          ))}
                          {warning.codeSnippets.length > 3 && (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>... and {warning.codeSnippets.length - 3} more matches</div>
                          )}
                        </div>
                      )}

                      {/* Load Code Snippet Button */}
                      {filePath && lineNumber && (
                        <button
                          onClick={() => fetchCodeSnippet(filePath, lineNumber, idx)}
                          disabled={loadingSnippet === cacheKey}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: snippet ? '#4caf50' : '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loadingSnippet === cacheKey ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginBottom: '12px',
                            width: '100%'
                          }}
                        >
                          {loadingSnippet === cacheKey ? '⏳ Loading Code...' : snippet ? '✓ Code Loaded - Click to Reload' : '📄 Show Code Snippet'}
                        </button>
                      )}

                      {/* Code Snippet Display */}
                      {snippet && !snippet.error && (
                        <div style={{
                          backgroundColor: '#1a1a1a',
                          border: '3px solid #d32f2f',
                          borderRadius: '4px',
                          padding: '12px',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          overflow: 'auto',
                          maxHeight: '250px',
                          marginBottom: '12px',
                          lineHeight: '1.6',
                          color: '#ffffff'
                        }}>
                          {/* Before Code */}
                          {snippet.beforeCode?.length > 0 && (
                            <div style={{ marginBottom: '6px' }}>
                              {snippet.beforeCode.map((line, i) => (
                                <div key={i}><span style={{ color: '#888', fontSize: '11px' }}>...</span> <span style={{ color: '#ffffff' }}>{line}</span></div>
                              ))}
                            </div>
                          )}

                          {/* Problem Code - HIGHLIGHTED */}
                          {snippet.code ? (
                            <div style={{
                              backgroundColor: '#d32f2f',
                              border: '2px solid #9c1f1f',
                              padding: '10px',
                              borderRadius: '4px',
                              marginBottom: '6px',
                              fontWeight: 'bold'
                            }}>
                              <div style={{ color: '#ffff00', marginBottom: '6px', fontSize: '11px', fontWeight: 'bold' }}>▶ Line {lineNumber}:</div>
                              <div style={{ color: '#000000', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 'bold', fontSize: '12px' }}>
                                {snippet.code}
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              backgroundColor: '#d32f2f',
                              border: '2px solid #9c1f1f',
                              padding: '10px',
                              borderRadius: '4px',
                              marginBottom: '6px',
                              color: '#ffff00',
                              fontSize: '12px'
                            }}>
                              ⚠️ Code line not found (line number may be incorrect)
                            </div>
                          )}

                          {/* After Code */}
                          {snippet.afterCode?.length > 0 && (
                            <div style={{ marginTop: '6px' }}>
                              {snippet.afterCode.map((line, i) => (
                                <div key={i}><span style={{ color: '#888', fontSize: '11px' }}>...</span> <span style={{ color: '#ffffff' }}>{line}</span></div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Error Display */}
                      {snippet?.error && (
                        <div>
                          <div style={{
                            backgroundColor: '#ffebee',
                            border: '2px solid #d32f2f',
                            borderRadius: '4px',
                            padding: '12px',
                            marginBottom: '12px',
                            color: '#c62828',
                            fontSize: '12px'
                          }}>
                            ❌ Error loading code: {snippet.error}
                          </div>
                          <button
                            onClick={async () => {
                              const apiUrl = getApiBase();
                              const urlMatch = prRisk.prUrl?.match(/github\.com\/([^/]+)\/([^/]+)\/pull\/(\d+)/);
                              const owner = urlMatch?.[1];
                              const repo = urlMatch?.[2];
                              const prNumber = prRisk.prNumber;
                              
                              console.log(`[DIAGNOSTIC] Testing PR files for: ${owner}/${repo}/PR#${prNumber}`);
                              console.log(`[DIAGNOSTIC] Full PR URL from prRisk: ${prRisk.prUrl}`);
                              try {
                                const headers = {
                                  'Content-Type': 'application/json'
                                };
                                const response = await fetch(`${apiUrl}/api/debug/pr-files/${owner}/${repo}/${prNumber}`, { headers });
                                const data = await response.json();
                                console.log('[DIAGNOSTIC] PR Files Response:', data);
                                if (data.success) {
                                  alert(`✅ Diagnostic Complete!\n\nPR #${prNumber} has ${data.totalFiles} files:\n\n${data.files?.map(f => f.filename).join('\n')}\n\nCheck console for full details.`);
                                } else {
                                  alert(`❌ Could not fetch PR files\n\nRepository: ${owner}/${repo}\nPR: #${prNumber}\n\nError: ${data.note || data.error}\n\nCheck console for details.`);
                                }
                              } catch (error) {
                                console.error('[DIAGNOSTIC] Error:', error);
                                alert(`❌ Diagnostic Failed: ${error.message}\n\nCheck the console for more details.\n\nMake sure the GitHub URL is correct: ${prRisk.prUrl}`);
                              }
                            }}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#ff9800',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              marginBottom: '12px',
                              width: '100%'
                            }}
                          >
                            🔧 Diagnose - Check Available Files
                          </button>
                        </div>
                      )}

                      {/* Ask AI Button */}
                      {filePath && lineNumber && snippet && !snippet.error && (
                        <button
                          onClick={() => askAIValidation(warning, idx, snippet)}
                          disabled={askingAI === cacheKey}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: validation ? '#9c27b0' : '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: askingAI === cacheKey ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginBottom: '12px',
                            width: '100%'
                          }}
                        >
                          {askingAI === cacheKey ? '⏳ Asking AI...' : validation ? '✏️ Ask AI Again' : '🤖 Ask AI: Is This Really a Problem?'}
                        </button>
                      )}

                      {/* AI Validation Result */}
                      {validation && !validation.error && (
                        <div style={{
                          backgroundColor: validation.isRealIssue ? '#ffebee' : '#e8f5e9',
                          border: `2px solid ${validation.isRealIssue ? '#d32f2f' : '#4caf50'}`,
                          borderRadius: '4px',
                          padding: '12px',
                          marginBottom: '12px'
                        }}>
                          <div style={{
                            fontWeight: 'bold',
                            color: validation.isRealIssue ? '#c62828' : '#2e7d32',
                            marginBottom: '8px',
                            fontSize: '13px'
                          }}>
                            {validation.isRealIssue ? '🚨 AI: This IS a Real Issue' : '✅ AI: This is a False Positive'}
                          </div>
                          
                          {validation.analysis ? (
                            <div style={{ fontSize: '12px', color: '#333', marginBottom: '8px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                              {validation.analysis}
                            </div>
                          ) : (
                            <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
                              [No analysis provided]
                            </div>
                          )}

                          {validation.recommendation ? (
                            <div style={{
                              backgroundColor: '#fff3cd',
                              border: '1px solid #ffc107',
                              borderRadius: '4px',
                              padding: '8px',
                              marginTop: '8px',
                              fontSize: '12px',
                              color: '#856404',
                              whiteSpace: 'pre-wrap'
                            }}>
                              <strong>💡 Recommendation:</strong> {validation.recommendation}
                            </div>
                          ) : null}
                        </div>
                      )}
                      {validation && validation.error && (
                        <div style={{
                          backgroundColor: '#ffebee',
                          border: '2px solid #d32f2f',
                          borderRadius: '4px',
                          padding: '12px',
                          marginBottom: '12px',
                          color: '#c62828',
                          fontSize: '12px'
                        }}>
                          ❌ Error: {validation.error}
                        </div>
                      )}

                      {/* GitHub Link */}
                      {!isString && warning.link && (
                        <a
                          href={warning.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            marginTop: '8px',
                            padding: '6px 12px',
                            backgroundColor: severityColor,
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            width: '100%',
                            textAlign: 'center',
                            boxSizing: 'border-box'
                          }}
                        >
                          🔗 View on GitHub
                        </a>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        )}

        {/* GitHub Link Section */}
        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
          <a
            href={`${prRisk.prUrl || `https://github.com/ischemaview/ZyraAi/pull/${prRisk.prNumber}`}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '10px 16px',
              backgroundColor: '#24292e',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontWeight: 'bold',
              fontSize: '13px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1b1f23'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#24292e'}
          >
            🔗 View on GitHub
          </a>
        </div>

        <div style={{ marginBottom: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>PR Metrics:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
              <strong>Unresolved Comments:</strong> {prRisk.unresolvedComments || 0}
            </p>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
              <strong>Reviews:</strong> {prRisk.reviewCount || 0}
            </p>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
              <strong>Merge Time:</strong> {prRisk.mergeTimeHours != null && prRisk.mergeTimeHours > 0 ? `${prRisk.mergeTimeHours.toFixed(1)}h` : prRisk.mergeTimeHours === 0 ? '< 1h' : 'N/A'}
            </p>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
              <strong>Risk Level:</strong> <span style={{ color: getRiskColor(prRisk.riskLevel), fontWeight: 'bold' }}>{prRisk.riskLevel.toUpperCase()}</span>
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
          }}
        >
          Close
        </button>
      </div>

      {/* Risk Explanation Modal */}
      {showRiskExplanation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setShowRiskExplanation(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Risk Explanation</h3>
              <button
                onClick={() => setShowRiskExplanation(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', borderLeft: '3px solid #ffc107', borderRadius: '4px' }}>
              <strong>Risk:</strong> {selectedRiskForExplanation}
            </div>

            {explainRiskLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{ fontSize: '18px', marginBottom: '12px' }}>🤖 AI is analyzing this risk...</div>
                <div style={{ fontSize: '12px' }}>Please wait for the explanation</div>
              </div>
            ) : (
              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px', lineHeight: '1.6', color: '#333', fontSize: '14px' }}>
                {riskExplanation ? (
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {riskExplanation}
                  </div>
                ) : (
                  <div style={{ color: '#999' }}>No explanation available</div>
                )}
              </div>
            )}

            <button
              onClick={() => {
                setRiskExplanation(null);
                askAIExplainRisk(selectedRiskForExplanation);
              }}
              disabled={explainRiskLoading}
              style={{
                width: '48%',
                padding: '10px',
                backgroundColor: explainRiskLoading ? '#ccc' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: explainRiskLoading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '13px',
                marginRight: '4%'
              }}
            >
              {explainRiskLoading ? 'Loading...' : 'Ask Again'}
            </button>
            <button
              onClick={() => setShowRiskExplanation(false)}
              style={{
                width: '48%',
                padding: '10px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '13px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Risk Badge Component
function RiskBadge({ riskLevel, riskScore }) {
  const getRiskColor = (level) => {
    switch (level) {
      case 'critical':
        return '#d32f2f';
      case 'high':
        return '#f57c00';
      case 'medium':
        return '#fbc02d';
      case 'low':
        return '#388e3c';
      default:
        return '#999';
    }
  };

  const getTextColor = (level) => {
    return level === 'medium' ? '#333' : '#fff';
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '12px',
        backgroundColor: getRiskColor(riskLevel),
        color: getTextColor(riskLevel),
        fontWeight: 'bold',
        fontSize: '12px',
      }}
    >
      {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} Risk
    </span>
  );
}

export default PullRequestForm;

// ============================================
// BRANCH REVIEW PANEL
// ============================================
function BranchReviewPanel({
  selectedRepo, selectedModel, authenticatedFetch,
  branches, setBranches, selectedBranch, setSelectedBranch,
  branchSearch, setBranchSearch, loadingBranches, setLoadingBranches,
  branchAnalysis, setBranchAnalysis, isAnalyzingBranch, setIsAnalyzingBranch,
  branchError, setBranchError,
}) {
  const apiUrl = getApiBase();
  const [rulesContent, setRulesContent] = React.useState('');
  const [rulesFileName, setRulesFileName] = React.useState('');
  const rulesFileRef = React.useRef(null);

  const fetchBranches = async () => {
    if (!selectedRepo) return;
    setLoadingBranches(true);
    setBranchError(null);
    try {
      const [owner, repo] = selectedRepo.split('/');
      const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/branches`);
      const data = await response.json();
      if (data.success) setBranches(data.branches || []);
      else throw new Error(data.error || 'Failed to fetch branches');
    } catch (err) { setBranchError(err.message); }
    finally { setLoadingBranches(false); }
  };

  const analyzeBranch = async () => {
    if (!selectedRepo || !selectedBranch) return;
    setIsAnalyzingBranch(true);
    setBranchError(null);
    setBranchAnalysis(null);
    try {
      const [owner, repo] = selectedRepo.split('/');
      const body = { branch: selectedBranch, selectedModel: selectedModel || 'gemini-2.5-flash' };
      if (rulesContent) { body.rulesContent = rulesContent; body.rulesFileName = rulesFileName; }
      const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/analyze-branch`, {
        method: 'POST', body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.success !== false) setBranchAnalysis(data);
      else throw new Error(data.error || 'Analysis failed');
    } catch (err) { setBranchError(err.message); }
    finally { setIsAnalyzingBranch(false); }
  };

  const handleRulesUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRulesFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setRulesContent(ev.target.result);
    reader.readAsText(file);
  };

  const handleEmail = () => {
    const a = branchAnalysis;
    const hasRules = !!a.verdict;
    const lines = [
      `BRANCH REVIEW REPORT`, `${'='.repeat(30)}`, ``,
      `Repository: ${selectedRepo}`, `Branch: ${selectedBranch}`, `Date: ${new Date().toLocaleString()}`,
    ];
    if (rulesFileName) lines.push(`Rules: ${rulesFileName}`);
    lines.push('');
    if (hasRules) {
      lines.push(`Verdict: ${a.verdict}`, `Rules Compliance: ${a.rulesCompliance}%`, `Risk Level: ${a.riskLevel}%`, `Overall Score: ${a.overallScore}%`, '');
      lines.push(`Files Changed: ${a.filesChanged || 0}`, `Additions: +${a.additionsCount || 0}`, `Deletions: -${a.deletionsCount || 0}`, '');
      if (a.violations?.length) { lines.push(`VIOLATIONS (${a.violations.length})`); a.violations.forEach(v => lines.push(`- [${v.severity}] ${v.rule}: ${v.message}`)); lines.push(''); }
      if (a.recommendations?.length) { lines.push(`RECOMMENDATIONS`); a.recommendations.forEach(r => lines.push(`- ${r}`)); }
    } else {
      lines.push(a.analysis || '');
    }
    const subject = encodeURIComponent(`Branch Review: ${selectedBranch} — ${selectedRepo}`);
    const body = encodeURIComponent(lines.join('\n'));
    const w = window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    if (!w) window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  React.useEffect(() => { if (selectedRepo && branches.length === 0) fetchBranches(); }, [selectedRepo]);

  const filteredBranches = branches.filter(b => b.name.toLowerCase().includes(branchSearch.toLowerCase()));

  if (!selectedRepo) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: '4px' }}>
        <strong style={{ color: '#e65100' }}>⚠️ No Repository Selected</strong>
        <p style={{ color: '#d84315', margin: '8px 0 0', fontSize: '13px' }}>Select a repository from the <strong>Pull Request Analysis</strong> tab first.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ color: '#000', fontWeight: 'bold', marginBottom: '15px' }}>🌿 Branch Review</h3>
      <p style={{ color: '#666', marginBottom: '20px', fontSize: '13px' }}>
        Select a branch to run AI-powered analysis. Optionally upload a rules/guidelines file for structured compliance checking.
      </p>

      {branchError && <div style={{ padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '15px' }}>{branchError}</div>}

      {/* Rules File Upload */}
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px dashed #ccc', borderRadius: '6px', backgroundColor: '#fafafa' }}>
        <label style={{ fontWeight: 'bold', fontSize: '14px', display: 'block', marginBottom: '8px' }}>📋 Rules / Guidelines File (optional)</label>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>Upload a .md, .txt, or .json rules file. The analysis will check the branch against these rules and produce a structured compliance report.</p>
        <input ref={rulesFileRef} type="file" accept=".md,.txt,.json,.yaml,.yml" onChange={handleRulesUpload} style={{ fontSize: '13px' }} />
        {rulesFileName && (
          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '12px', color: '#2e7d32', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>✅ Loaded: <strong>{rulesFileName}</strong> ({rulesContent.length} bytes)</span>
            <button onClick={() => { setRulesContent(''); setRulesFileName(''); if (rulesFileRef.current) rulesFileRef.current.value = ''; }} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>✕ Clear</button>
          </div>
        )}
      </div>

      {/* Branch Selector */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ fontWeight: 'bold', fontSize: '14px' }}>1️⃣ Select Branch</label>
          <button onClick={fetchBranches} disabled={loadingBranches} style={{ padding: '6px 14px', backgroundColor: '#667eea', color: '#fff', border: 'none', borderRadius: '4px', cursor: loadingBranches ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold', opacity: loadingBranches ? 0.6 : 1 }}>
            {loadingBranches ? '⏳ Loading...' : '🔄 Refresh'}
          </button>
        </div>
        <input type="text" placeholder="Search branches..." value={branchSearch} onChange={(e) => setBranchSearch(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '14px' }} />
        {loadingBranches ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>⏳ Loading branches...</div>
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
            {filteredBranches.map((branch) => (
              <div key={branch.name} onClick={() => setSelectedBranch(branch.name)} style={{ padding: '12px 15px', cursor: 'pointer', backgroundColor: selectedBranch === branch.name ? '#e3f2fd' : '#fff', borderBottom: '1px solid #f0f0f0', borderLeft: selectedBranch === branch.name ? '3px solid #667eea' : '3px solid transparent', transition: 'all 0.15s' }}>
                <div style={{ fontWeight: selectedBranch === branch.name ? 'bold' : 'normal', fontSize: '13px', color: '#333' }}>🌿 {branch.name}</div>
                {branch.lastCommitDate && <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>Last commit: {new Date(branch.lastCommitDate).toLocaleDateString()} {new Date(branch.lastCommitDate).toLocaleTimeString()}</div>}
              </div>
            ))}
            {filteredBranches.length === 0 && <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>No branches match</div>}
          </div>
        )}
        {branches.length > 0 && <div style={{ fontSize: '11px', color: '#999', marginTop: '5px' }}>{filteredBranches.length} of {branches.length} branches</div>}
      </div>

      {/* Analyze Button */}
      {selectedBranch && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px', marginBottom: '10px', fontSize: '13px', color: '#2e7d32' }}>
            ✅ Selected: <strong>{selectedBranch}</strong>{rulesFileName ? ` • Rules: ${rulesFileName}` : ' • No rules (AI-only analysis)'}
          </div>
          <button onClick={analyzeBranch} disabled={isAnalyzingBranch} style={{ padding: '12px 24px', backgroundColor: isAnalyzingBranch ? '#ccc' : '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: isAnalyzingBranch ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px', width: '100%' }}>
            {isAnalyzingBranch ? '⏳ Analyzing branch...' : `🔍 Analyze Branch: ${selectedBranch}`}
          </button>
        </div>
      )}

      {/* Risk Summary Dashboard (rules mode) */}
      {branchAnalysis && branchAnalysis.verdict && <RiskSummaryDashboard result={branchAnalysis} label={`Branch: ${selectedBranch}`} />}

      {/* Structured Results (rules mode) */}
      {branchAnalysis && branchAnalysis.verdict && <StructuredResultsPanel result={branchAnalysis} label={`Branch: ${selectedBranch}`} rulesFileName={rulesFileName} onEmail={handleEmail} />}

      {/* AI-only Results (no rules) — still show a lightweight dashboard */}
      {branchAnalysis && !branchAnalysis.verdict && (
        <>
          {/* Risk Summary Dashboard — always shown */}
          <RiskSummaryDashboard result={branchAnalysis} label={`Branch: ${selectedBranch}`} />

          {/* Quick snapshot for AI-only mode */}
          <div style={{ marginBottom: '20px', padding: '20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '8px', color: 'white' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>📊 Branch Analysis Dashboard — {selectedBranch}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
              <div style={{ padding: '12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{branchAnalysis.filesChanged || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.9 }}>📂 Files Changed</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#a5d6a7' }}>+{branchAnalysis.additionsCount || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.9 }}>Additions</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#ef9a9a' }}>-{branchAnalysis.deletionsCount || 0}</div>
                <div style={{ fontSize: '11px', opacity: 0.9 }}>Deletions</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{branchAnalysis.branch?.commitsAnalyzed || '—'}</div>
                <div style={{ fontSize: '11px', opacity: 0.9 }}>Commits</div>
              </div>
            </div>
          </div>
          <div style={{ padding: '20px', border: '1px solid #28a745', borderRadius: '8px', backgroundColor: '#f4fff4' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ color: '#000', margin: 0 }}>🌿 Branch Analysis: <code style={{ backgroundColor: '#e8e8e8', padding: '2px 6px', borderRadius: '3px' }}>{branchAnalysis.branch?.name}</code></h4>
              <button onClick={handleEmail} style={{ padding: '6px 14px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>📧 Email Report</button>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#333', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: formatMarkdown(branchAnalysis.analysis) }} />
          </div>
        </>
      )}
    </div>
  );
}

// ============================================
// FILE ANALYSIS PANEL
// ============================================
function FileAnalysisPanel({
  selectedRepo, selectedModel, authenticatedFetch,
  fileTree, setFileTree, fileTreePath, setFileTreePath,
  fileTreeRef, setFileTreeRef,
  selectedAnalysisFile, setSelectedAnalysisFile,
  fileAnalysis, setFileAnalysis, isAnalyzingFile, setIsAnalyzingFile,
  fileAnalysisError, setFileAnalysisError,
  loadingFileTree, setLoadingFileTree,
  fileTreeBreadcrumb, setFileTreeBreadcrumb,
  branches, setBranches,
}) {
  const apiUrl = getApiBase();
  const [rulesContent, setRulesContent] = React.useState('');
  const [rulesFileName, setRulesFileName] = React.useState('');
  const rulesFileRef = React.useRef(null);

  const fetchBranches = async () => {
    if (!selectedRepo || branches.length > 0) return;
    try {
      const [owner, repo] = selectedRepo.split('/');
      const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/branches`);
      const data = await response.json();
      if (data.success) setBranches(data.branches || []);
    } catch (_) {}
  };

  const fetchTree = async (path = '', ref = fileTreeRef) => {
    if (!selectedRepo) return;
    setLoadingFileTree(true);
    setFileAnalysisError(null);
    try {
      const [owner, repo] = selectedRepo.split('/');
      let url = `${apiUrl}/api/github/repos/${owner}/${repo}/contents`;
      const params = new URLSearchParams();
      if (ref) params.set('ref', ref);
      if (path) params.set('path', path);
      if (params.toString()) url += `?${params}`;
      const response = await authenticatedFetch(url);
      const data = await response.json();
      if (data.success) {
        const items = (data.contents || []).sort((a, b) => {
          if (a.type === 'dir' && b.type !== 'dir') return -1;
          if (a.type !== 'dir' && b.type === 'dir') return 1;
          return a.name.localeCompare(b.name);
        });
        setFileTree(items);
        setFileTreePath(path);
        const parts = path ? path.split('/') : [];
        const crumbs = [{ name: '📂 root', path: '' }];
        let cum = '';
        parts.forEach(p => { cum = cum ? `${cum}/${p}` : p; crumbs.push({ name: p, path: cum }); });
        setFileTreeBreadcrumb(crumbs);
      } else throw new Error(data.error || 'Failed to fetch contents');
    } catch (err) { setFileAnalysisError(err.message); }
    finally { setLoadingFileTree(false); }
  };

  const analyzeFile = async () => {
    if (!selectedRepo || !selectedAnalysisFile) return;
    setIsAnalyzingFile(true);
    setFileAnalysisError(null);
    setFileAnalysis(null);
    try {
      const [owner, repo] = selectedRepo.split('/');
      const body = { filePath: selectedAnalysisFile, selectedModel: selectedModel || 'gemini-2.5-flash' };
      if (fileTreeRef) body.ref = fileTreeRef;
      if (rulesContent) { body.rulesContent = rulesContent; body.rulesFileName = rulesFileName; }
      const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${owner}/${repo}/analyze-file`, {
        method: 'POST', body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.success !== false) setFileAnalysis(data);
      else throw new Error(data.error || 'Analysis failed');
    } catch (err) { setFileAnalysisError(err.message); }
    finally { setIsAnalyzingFile(false); }
  };

  const handleRulesUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRulesFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setRulesContent(ev.target.result);
    reader.readAsText(file);
  };

  const handleEmail = () => {
    const a = fileAnalysis;
    const hasRules = !!a.verdict;
    const lines = [
      `FILE ANALYSIS REPORT`, `${'='.repeat(30)}`, ``,
      `Repository: ${selectedRepo}`, `File: ${selectedAnalysisFile}`, `Date: ${new Date().toLocaleString()}`,
    ];
    if (rulesFileName) lines.push(`Rules: ${rulesFileName}`);
    lines.push('');
    if (hasRules) {
      lines.push(`Verdict: ${a.verdict}`, `Rules Compliance: ${a.rulesCompliance}%`, `Risk Level: ${a.riskLevel}%`, `Overall Score: ${a.overallScore}%`, '');
      if (a.violations?.length) { lines.push(`VIOLATIONS (${a.violations.length})`); a.violations.forEach(v => lines.push(`- [${v.severity}] ${v.rule}: ${v.message}`)); lines.push(''); }
      if (a.recommendations?.length) { lines.push(`RECOMMENDATIONS`); a.recommendations.forEach(r => lines.push(`- ${r}`)); lines.push(''); }
    }
    if (a.analysis) lines.push('AI ANALYSIS', '-'.repeat(20), a.analysis);
    const subject = encodeURIComponent(`File Analysis: ${selectedAnalysisFile} — ${selectedRepo}`);
    const body = encodeURIComponent(lines.join('\n'));
    const w = window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    if (!w) window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  React.useEffect(() => { if (selectedRepo) { fetchBranches(); if (fileTree.length === 0) fetchTree(''); } }, [selectedRepo]);

  const isCodeFile = (name) => {
    const ext = ['.js', '.jsx', '.ts', '.tsx', '.py', '.go', '.java', '.rb', '.php', '.cpp', '.c', '.h', '.cs', '.rs', '.sh', '.sql', '.yaml', '.yml', '.json', '.md', '.html', '.css', '.scss'];
    return ext.some(e => name.toLowerCase().endsWith(e));
  };

  if (!selectedRepo) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: '4px' }}>
        <strong style={{ color: '#e65100' }}>⚠️ No Repository Selected</strong>
        <p style={{ color: '#d84315', margin: '8px 0 0', fontSize: '13px' }}>Select a repository from the <strong>Pull Request Analysis</strong> tab first.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ color: '#000', fontWeight: 'bold', marginBottom: '15px' }}>📄 File Analysis</h3>
      <p style={{ color: '#666', marginBottom: '20px', fontSize: '13px' }}>
        Browse the repository, select a file, and run AI analysis. Upload a rules file for structured compliance checking.
      </p>

      {fileAnalysisError && <div style={{ padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '15px' }}>{fileAnalysisError}</div>}

      {/* Rules File Upload */}
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px dashed #ccc', borderRadius: '6px', backgroundColor: '#fafafa' }}>
        <label style={{ fontWeight: 'bold', fontSize: '14px', display: 'block', marginBottom: '8px' }}>📋 Rules / Guidelines File (optional)</label>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>Upload a .md, .txt, or .json rules file for structured compliance analysis.</p>
        <input ref={rulesFileRef} type="file" accept=".md,.txt,.json,.yaml,.yml" onChange={handleRulesUpload} style={{ fontSize: '13px' }} />
        {rulesFileName && (
          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '12px', color: '#2e7d32', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>✅ Loaded: <strong>{rulesFileName}</strong> ({rulesContent.length} bytes)</span>
            <button onClick={() => { setRulesContent(''); setRulesFileName(''); if (rulesFileRef.current) rulesFileRef.current.value = ''; }} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>✕ Clear</button>
          </div>
        )}
      </div>

      {/* Branch Picker */}
      {branches.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ fontWeight: 'bold', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Branch:</label>
          <select value={fileTreeRef} onChange={(e) => { setFileTreeRef(e.target.value); setSelectedAnalysisFile(''); setFileAnalysis(null); fetchTree('', e.target.value); }} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', backgroundColor: '#fff' }}>
            <option value="">Default branch</option>
            {branches.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
          </select>
        </div>
      )}

      {/* Breadcrumb */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', marginBottom: '10px', fontSize: '13px' }}>
        {fileTreeBreadcrumb.map((crumb, i) => (
          <React.Fragment key={crumb.path}>
            {i > 0 && <span style={{ color: '#ccc' }}>/</span>}
            <span onClick={() => { fetchTree(crumb.path); setSelectedAnalysisFile(''); setFileAnalysis(null); }} style={{ color: '#667eea', cursor: 'pointer', fontWeight: i === fileTreeBreadcrumb.length - 1 ? 'bold' : 'normal' }}>{crumb.name}</span>
          </React.Fragment>
        ))}
      </div>

      {/* File Tree */}
      {loadingFileTree ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>⏳ Loading...</div>
      ) : (
        <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '15px' }}>
          {fileTree.map((item) => {
            const fullPath = fileTreePath ? `${fileTreePath}/${item.name}` : item.name;
            return (
              <div key={item.name} onClick={() => {
                if (item.type === 'dir') { fetchTree(fullPath); setSelectedAnalysisFile(''); setFileAnalysis(null); }
                else if (isCodeFile(item.name)) { setSelectedAnalysisFile(fullPath); setFileAnalysis(null); }
              }} style={{
                padding: '10px 15px', cursor: item.type === 'dir' || isCodeFile(item.name) ? 'pointer' : 'default',
                backgroundColor: selectedAnalysisFile === fullPath ? '#e3f2fd' : '#fff',
                borderBottom: '1px solid #f0f0f0', borderLeft: selectedAnalysisFile === fullPath ? '3px solid #667eea' : '3px solid transparent',
                opacity: item.type !== 'dir' && !isCodeFile(item.name) ? 0.5 : 1, transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <span style={{ fontSize: '16px' }}>{item.type === 'dir' ? '📁' : '📄'}</span>
                <span style={{ fontSize: '13px', color: '#333' }}>{item.name}</span>
                {item.size && item.type !== 'dir' && <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>{item.size > 1024 ? `${(item.size / 1024).toFixed(1)} KB` : `${item.size} B`}</span>}
              </div>
            );
          })}
          {fileTree.length === 0 && <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>Empty directory</div>}
        </div>
      )}

      {/* Analyze Button */}
      {selectedAnalysisFile && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px', marginBottom: '10px', fontSize: '13px', color: '#2e7d32' }}>
            ✅ Selected: <strong>{selectedAnalysisFile}</strong>{rulesFileName ? ` • Rules: ${rulesFileName}` : ' • No rules (AI-only analysis)'}
          </div>
          <button onClick={analyzeFile} disabled={isAnalyzingFile} style={{ padding: '12px 24px', backgroundColor: isAnalyzingFile ? '#ccc' : '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: isAnalyzingFile ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '14px', width: '100%' }}>
            {isAnalyzingFile ? '⏳ Analyzing file...' : `🔍 Analyze: ${selectedAnalysisFile.split('/').pop()}`}
          </button>
        </div>
      )}

      {/* Risk Summary Dashboard (rules mode) */}
      {fileAnalysis && fileAnalysis.verdict && <RiskSummaryDashboard result={fileAnalysis} label={`File: ${selectedAnalysisFile}`} />}

      {/* Structured Results (rules mode) */}
      {fileAnalysis && fileAnalysis.verdict && <StructuredResultsPanel result={fileAnalysis} label={`File: ${selectedAnalysisFile}`} rulesFileName={rulesFileName} onEmail={handleEmail} />}

      {/* AI-only Results (no rules) — lightweight dashboard */}
      {fileAnalysis && !fileAnalysis.verdict && (
        <>
          <div style={{ marginBottom: '20px', padding: '20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '8px', color: 'white' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>📊 File Analysis Dashboard — {selectedAnalysisFile.split('/').pop()}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
              <div style={{ padding: '12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedAnalysisFile}</div>
                <div style={{ fontSize: '11px', opacity: 0.9 }}>📄 File Analyzed</div>
              </div>
              {fileAnalysis.file?.size && (
                <div style={{ padding: '12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{fileAnalysis.file.size > 1024 ? `${(fileAnalysis.file.size / 1024).toFixed(1)} KB` : `${fileAnalysis.file.size} B`}</div>
                  <div style={{ fontSize: '11px', opacity: 0.9 }}>File Size</div>
                </div>
              )}
            </div>
          </div>
          <div style={{ padding: '20px', border: '1px solid #28a745', borderRadius: '8px', backgroundColor: '#f4fff4' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ color: '#000', margin: 0 }}>📄 File Analysis: <code style={{ backgroundColor: '#e8e8e8', padding: '2px 6px', borderRadius: '3px' }}>{fileAnalysis.file?.path}</code></h4>
              <button onClick={handleEmail} style={{ padding: '6px 14px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>📧 Email Report</button>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#333', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: formatMarkdown(fileAnalysis.analysis) }} />
          </div>
        </>
      )}
    </div>
  );
}

// ============================================
// RISK SUMMARY DASHBOARD — matches PR Analysis dashboard
// ============================================
function RiskSummaryDashboard({ result, label }) {
  // Use backend riskSummary (from PR risk analysis) when available — identical to PR Analysis dashboard
  const rs = result.riskSummary;

  // Fall back to deriving from violations if no riskSummary from backend
  const lowRisks = rs ? rs.lowRisks : (result.violations || []).filter(v => v.severity === 'low').length;
  const mediumRisks = rs ? rs.mediumRisks : (result.violations || []).filter(v => v.severity === 'medium').length;
  const highRisks = rs ? rs.highRisks : (result.violations || []).filter(v => v.severity === 'high').length;
  const criticalRisks = rs ? rs.criticalRisks : (result.violations || []).filter(v => v.severity === 'critical').length;

  // Synthesize risk score from branch stats when no riskSummary or violations
  const synthesizeRiskScore = () => {
    const files = result.filesChanged || 0;
    const additions = result.additionsCount || 0;
    const deletions = result.deletionsCount || 0;
    const totalChanges = additions + deletions;
    let score = 0;
    if (files > 20) score += 20;
    else if (files > 10) score += 10;
    else if (files > 5) score += 5;
    if (totalChanges > 1000) score += 25;
    else if (totalChanges > 500) score += 15;
    else if (totalChanges > 200) score += 8;
    else if (totalChanges > 50) score += 3;
    return Math.min(100, score);
  };

  const avgRiskScore = rs ? rs.avgRiskScore : (result.riskLevel != null ? Math.round(result.riskLevel) : synthesizeRiskScore());

  // Most common risks — from riskSummary or derived from violations
  let mostCommonRisks = {};
  if (rs && rs.mostCommonRisks) {
    mostCommonRisks = rs.mostCommonRisks;
  } else {
    (result.violations || []).forEach(v => {
      const key = v.rule || v.title || 'Unknown';
      mostCommonRisks[key] = (mostCommonRisks[key] || 0) + 1;
    });
  }

  // If no risks at all, add contextual info from branch stats
  if (Object.keys(mostCommonRisks).length === 0 && !rs) {
    const files = result.filesChanged || 0;
    const additions = result.additionsCount || 0;
    if (files > 10) mostCommonRisks['📂 Large number of files changed'] = files;
    if (additions > 500) mostCommonRisks['📊 High line count change'] = additions;
    if (files <= 10 && additions <= 500) mostCommonRisks['✅ Small, focused changeset'] = 1;
  }
  const sortedRisks = Object.entries(mostCommonRisks).sort((a, b) => b[1] - a[1]);

  // Recommendations — from riskSummary or result or synthesized
  let recommendations = rs ? (rs.recommendations || []) : (result.recommendations || []);
  if (recommendations.length === 0 && !rs) {
    const files = result.filesChanged || 0;
    const additions = result.additionsCount || 0;
    if (files > 15) recommendations = ['⚠️ Consider breaking this branch into smaller, focused PRs for easier review.'];
    else if (additions > 500) recommendations = ['📊 Large changeset — ensure thorough code review before merging.'];
    else recommendations = ['✅ Risk indicators are healthy. Continue monitoring current practices.'];
  }

  const totalPRs = rs ? (rs.totalPRsAnalyzed || 0) : 0;

  return (
    <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#f8f9fa', border: '2px solid #17a2b8', borderRadius: '8px' }}>
      <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#17a2b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
        🎯 Risk Analysis Summary
      </h4>
      {totalPRs > 0 && (
        <div style={{ marginBottom: '15px', padding: '8px 12px', backgroundColor: '#e8f5e9', borderRadius: '4px', fontSize: '12px', color: '#2e7d32' }}>
          📊 Based on {totalPRs} PR(s) associated with this branch
        </div>
      )}

      {/* Risk Distribution Cards — IDENTICAL to PR Analysis */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #28a745', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{lowRisks}</div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🟢 Low</div>
        </div>
        <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #ffc107', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>{mediumRisks}</div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🟡 Medium</div>
        </div>
        <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #fd7e14', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14' }}>{highRisks}</div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🟠 High</div>
        </div>
        <div style={{ padding: '15px', backgroundColor: '#fff', border: '2px solid #dc3545', borderRadius: '6px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>{criticalRisks}</div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>🔴 Critical</div>
        </div>
      </div>

      {/* Average Risk Score — IDENTICAL to PR Analysis */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <strong style={{ color: '#333' }}>Average Risk Score:</strong>
          <span style={{ fontSize: '18px', fontWeight: 'bold', color: avgRiskScore > 60 ? '#dc3545' : (avgRiskScore > 40 ? '#fd7e14' : '#28a745') }}>
            {avgRiskScore}/100
          </span>
        </div>
        <div style={{ width: '100%', height: '16px', backgroundColor: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ width: `${avgRiskScore}%`, height: '100%', backgroundColor: avgRiskScore > 60 ? '#dc3545' : (avgRiskScore > 40 ? '#fd7e14' : '#28a745'), transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* Most Common Risks — IDENTICAL to PR Analysis */}
      {sortedRisks.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#333', fontWeight: 'bold' }}>⚠️ Most Common Risks:</h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sortedRisks.slice(0, 5).map(([risk, count], idx) => (
              <div key={idx} style={{ padding: '10px', backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#333' }}>{risk}</span>
                <span style={{ fontSize: '11px', fontWeight: 'bold', backgroundColor: '#e9ecef', padding: '3px 8px', borderRadius: '12px', color: '#666' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations — IDENTICAL to PR Analysis */}
      {recommendations.length > 0 && (
        <div>
          <h5 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#333', fontWeight: 'bold' }}>💡 Recommendations:</h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {recommendations.slice(0, 5).map((rec, idx) => (
              <div key={idx} style={{ padding: '10px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '12px', color: '#1565c0', lineHeight: '1.4' }}>
                {typeof rec === 'string' ? rec : rec.recommendation || rec.text}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// RULES CHECKLIST PANEL — pass/fail per category and rule
// ============================================
function RulesChecklistPanel({ checklist, rulesFileName }) {
  if (!checklist || checklist.length === 0) return null;

  const totalCategories = checklist.length;
  const passedCategories = checklist.filter(c => c.passed).length;
  const totalRules = checklist.reduce((s, c) => s + c.totalRules, 0);
  const passedRules = checklist.reduce((s, c) => s + c.passedCount, 0);

  const severityColors = {
    critical: '#f44336', high: '#ff9800', medium: '#ffc107', low: '#4caf50',
  };

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginTop: '15px', marginBottom: '15px' }}>
      {/* Header */}
      <div style={{ padding: '15px 20px', backgroundColor: '#1a237e', color: '#fff' }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
          📋 Rules Compliance Checklist
        </div>
        <div style={{ fontSize: '13px', opacity: 0.9 }}>
          {rulesFileName ? `Rules: ${rulesFileName} • ` : ''}{passedCategories}/{totalCategories} categories passed • {passedRules}/{totalRules} rules passed
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div style={{ padding: '12px 20px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>Overall Compliance</span>
          <span style={{ fontSize: '14px', fontWeight: 'bold', color: passedRules === totalRules ? '#4caf50' : passedRules / totalRules > 0.7 ? '#ff9800' : '#f44336' }}>
            {totalRules > 0 ? Math.round((passedRules / totalRules) * 100) : 100}%
          </span>
        </div>
        <div style={{ width: '100%', height: '10px', backgroundColor: '#e0e0e0', borderRadius: '5px', overflow: 'hidden' }}>
          <div style={{
            width: `${totalRules > 0 ? (passedRules / totalRules) * 100 : 100}%`,
            height: '100%',
            backgroundColor: passedRules === totalRules ? '#4caf50' : passedRules / totalRules > 0.7 ? '#ff9800' : '#f44336',
            transition: 'width 0.3s',
          }} />
        </div>
      </div>

      {/* Categories */}
      {checklist.map((cat, catIdx) => (
        <details key={catIdx} style={{ borderBottom: '1px solid #e0e0e0' }} open={!cat.passed}>
          <summary style={{
            padding: '12px 20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: cat.passed ? '#f1f8e9' : '#fff3e0',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333',
          }}>
            <span style={{ fontSize: '18px' }}>{cat.passed ? '✅' : '❌'}</span>
            <span style={{ flex: 1 }}>{cat.categoryName}</span>
            <span style={{
              fontSize: '11px',
              fontWeight: 'bold',
              padding: '3px 10px',
              borderRadius: '12px',
              backgroundColor: cat.passed ? '#c8e6c9' : '#ffccbc',
              color: cat.passed ? '#2e7d32' : '#bf360c',
            }}>
              {cat.passedCount}/{cat.totalRules}
            </span>
            <span style={{
              fontSize: '10px',
              padding: '2px 8px',
              borderRadius: '8px',
              backgroundColor: severityColors[cat.severity] || '#9e9e9e',
              color: '#fff',
              textTransform: 'uppercase',
            }}>
              {cat.severity}
            </span>
          </summary>
          <div style={{ padding: '0' }}>
            {cat.rules.map((rule, ruleIdx) => (
              <div key={ruleIdx} style={{
                padding: '10px 20px 10px 50px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                borderBottom: ruleIdx < cat.rules.length - 1 ? '1px solid #f5f5f5' : 'none',
                backgroundColor: rule.passed ? '#fff' : '#fff8e1',
              }}>
                <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>
                  {rule.passed ? '✅' : '❌'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>
                    <span style={{ color: '#999', fontFamily: 'monospace', fontSize: '11px', marginRight: '6px' }}>{rule.id}</span>
                    {rule.name}
                  </div>
                  {!rule.passed && (
                    <div style={{ fontSize: '12px', color: '#d84315', marginTop: '4px', lineHeight: '1.4' }}>
                      {rule.violationCount} violation{rule.violationCount !== 1 ? 's' : ''} found
                      {rule.message && <span style={{ color: '#666' }}> — {rule.message}</span>}
                    </div>
                  )}
                  {!rule.passed && rule.fix && (
                    <div style={{ fontSize: '11px', color: '#1565c0', marginTop: '4px', padding: '6px 8px', backgroundColor: '#e3f2fd', borderRadius: '4px', lineHeight: '1.3' }}>
                      💡 Fix: {rule.fix}
                    </div>
                  )}
                </div>
                <span style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '6px',
                  backgroundColor: severityColors[rule.severity] || '#9e9e9e',
                  color: '#fff',
                  flexShrink: 0,
                  textTransform: 'uppercase',
                }}>
                  {rule.severity}
                </span>
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

// ============================================
// SHARED STRUCTURED RESULTS PANEL (scores, violations, recommendations, email)
// ============================================
function StructuredResultsPanel({ result, label, rulesFileName, onEmail }) {
  const verdictColors = { APPROVED: '#4caf50', APPROVED_WITH_COMMENTS: '#ff9800', CHANGES_REQUESTED: '#f44336', REVIEW_NEEDED: '#9e9e9e' };
  const verdictIcons = { APPROVED: '✅', APPROVED_WITH_COMMENTS: '⚠️', CHANGES_REQUESTED: '❌', REVIEW_NEEDED: '🔍' };

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginTop: '10px' }}>
      {/* Header */}
      <div style={{ padding: '15px 20px', backgroundColor: verdictColors[result.verdict] || '#667eea', color: '#fff' }}>
        <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
          {verdictIcons[result.verdict] || '📊'} {result.verdict?.replace(/_/g, ' ')}
        </div>
        <div style={{ fontSize: '13px', opacity: 0.9 }}>{label}{rulesFileName ? ` • Rules: ${rulesFileName}` : ''}</div>
      </div>

      {/* Score Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', backgroundColor: '#e0e0e0' }}>
        {[
          { label: 'Rules Compliance', value: `${result.rulesCompliance}%`, color: result.rulesCompliance >= 80 ? '#4caf50' : result.rulesCompliance >= 50 ? '#ff9800' : '#f44336' },
          { label: 'Risk Level', value: `${result.riskLevel}%`, color: result.riskLevel <= 20 ? '#4caf50' : result.riskLevel <= 50 ? '#ff9800' : '#f44336' },
          { label: 'Overall Score', value: `${result.overallScore}%`, color: result.overallScore >= 80 ? '#4caf50' : result.overallScore >= 50 ? '#ff9800' : '#f44336' },
        ].map(s => (
          <div key={s.label} style={{ padding: '15px', backgroundColor: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div style={{ padding: '15px 20px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.5' }}>{result.summary}</div>
        {result.filesChanged > 0 && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            📊 {result.filesChanged} file(s) • +{result.additionsCount || 0}/-{result.deletionsCount || 0} lines
          </div>
        )}
      </div>

      {/* Rules Checklist — pass/fail per category and rule (PRIMARY POSITION) */}
      {result.rulesChecklist && result.rulesChecklist.length > 0 && (
        <RulesChecklistPanel checklist={result.rulesChecklist} rulesFileName={rulesFileName} />
      )}

      {/* Violations */}
      {result.violations?.length > 0 && (
        <div style={{ padding: '15px 20px', borderBottom: '1px solid #e0e0e0' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#c62828' }}>
            🚫 Violations ({result.violations.length})
            {result.violationStats && <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666', marginLeft: '10px' }}>
              🔴 {result.violationStats.critical} • 🟠 {result.violationStats.high} • 🟡 {result.violationStats.medium} • 🔵 {result.violationStats.low}
            </span>}
          </h4>
          {result.violations.map((v, i) => (
            <details key={i} style={{ marginBottom: '8px', borderLeft: `3px solid ${v.severity === 'critical' ? '#f44336' : v.severity === 'high' ? '#ff9800' : v.severity === 'medium' ? '#ffc107' : '#2196f3'}`, paddingLeft: '12px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: '#333', padding: '6px 0' }}>
                [{v.severity?.toUpperCase()}] {v.rule || v.title}
              </summary>
              <div style={{ fontSize: '12px', color: '#666', padding: '6px 0 10px', lineHeight: '1.4' }}>
                {v.message || v.description}
                {v.file && <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>📁 {v.file}{v.line ? `:${v.line}` : ''}</div>}
              </div>
            </details>
          ))}
        </div>
      )}

      {/* Changed Files */}
      {result.changedFiles?.length > 0 && (
        <details style={{ borderBottom: '1px solid #e0e0e0' }}>
          <summary style={{ padding: '12px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', color: '#333', backgroundColor: '#fafafa' }}>
            📂 Changed Files ({result.changedFiles.length})
          </summary>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {result.changedFiles.map((f, i) => (
              <div key={i} style={{ padding: '8px 20px', fontSize: '12px', color: '#333', borderBottom: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between' }}>
                <span>{f.name}</span>
                <span style={{ color: '#999' }}>+{f.additions}/-{f.deletions}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Recommendations */}
      {result.recommendations?.length > 0 && (
        <div style={{ padding: '15px 20px', borderBottom: '1px solid #e0e0e0' }}>
          <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#1565c0' }}>💡 Recommendations</h4>
          {result.recommendations.map((r, i) => (
            <div key={i} style={{ padding: '8px 12px', backgroundColor: '#e3f2fd', borderLeft: '3px solid #2196f3', borderRadius: '4px', marginBottom: '6px', fontSize: '13px', color: '#1565c0', lineHeight: '1.4' }}>
              {typeof r === 'string' ? r : r.recommendation || r.text}
            </div>
          ))}
        </div>
      )}

      {/* AI Analysis (if present alongside rules) */}
      {result.analysis && (
        <details style={{ borderBottom: '1px solid #e0e0e0' }}>
          <summary style={{ padding: '12px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', color: '#333', backgroundColor: '#fafafa' }}>
            🤖 AI Detailed Analysis
          </summary>
          <div style={{ padding: '15px 20px', fontSize: '14px', lineHeight: '1.6', color: '#333', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: formatMarkdown(result.analysis) }} />
        </details>
      )}

      {/* Footer */}
      <div style={{ padding: '12px 20px', backgroundColor: '#f9f9f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '12px', color: '#999' }}>
          {rulesFileName ? `✅ Analysis using: ${rulesFileName}` : '🤖 AI analysis (no rules file)'}
        </div>
        <button onClick={onEmail} style={{ padding: '8px 16px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
          📧 Send Report via Email
        </button>
      </div>
    </div>
  );
}

// Simple markdown-to-HTML for analysis results
function formatMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:#e8e8e8;padding:1px 4px;border-radius:3px;font-size:13px">$1</code>')
    .replace(/^### (.+)$/gm, '<h4 style="margin:15px 0 8px;color:#333">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:18px 0 10px;color:#222">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="margin:20px 0 12px;color:#111">$1</h2>')
    .replace(/^- (.+)$/gm, '• $1<br/>')
    .replace(/^\d+\. (.+)$/gm, (_, p1, offset, str) => `${str.substring(0, offset).split('\n').filter(l => /^\d+\./.test(l)).length + 1}. ${p1}<br/>`)
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}
